/**
 * The bench is drawn from a LOW RAISED VIEWPOINT — the viewer stands a little
 * above it, the way you actually look at glassware on a table. Every
 * horizontal circle is therefore an ellipse of the SAME eccentricity
 * (vessel2D.ts's VIEW_TILT): a mouth, the liquid's surface in it, the floor
 * under that. One eye height for the whole scene is the entire rule — an
 * ellipse at one and a straight line at another is what reads as a bad 3D
 * render, not the ellipse itself. Vessel silhouettes come from the shared
 * profiles (vesselGeometry.ts via vessel2D.ts) and the apparatus from
 * Stands2D.tsx, which is held to the same eye height: a gauze is a plate you
 * see the top of, a clamp's ring an ellipse the vessel stands inside, a
 * burner's base a disc. VIEW_TILT is the one number both read, from
 * projection.ts.
 *
 * The bench is laid out in COLUMNS, not one-per-container: two neighbouring
 * containers on a multi-level stand share a single rod, stacked. benchLayout.ts
 * owns that grouping and every stand height, so the page can ask it the same
 * questions the scene does.
 *
 * The apparatus draws at a FIXED size up to three columns on a shelf; a
 * fourth zooms the WHOLE bench down together (see MIN_SCENE_ZOOM) rather
 * than scrolling — nothing here ever grows a horizontal scrollbar. Past four,
 * columns go on another shelf above instead of zooming further.
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChemistryLabOutcome } from '../types';
import { heldSubstances, liquidVolumeML, type LabContainer, type VesselContent } from '../labState';
import { pHBandToHex } from '../phPaperColor';
import { resolveColorHex } from '../colorMapping';
import { COLOURLESS, findSubstance, formatAmount, helpingsOf, perAddFor, type Substance, type SolidForm } from '../substanceCatalog';
import { fumeColorFor, headlineItems } from '../outcomeDisplay';
import { ChemEquation } from './ChemEquation';
import { conclusionOf } from './Working';
import { formatChemistry } from '../chemFormat';
import { countedMass, formatMass, formatMolarity, formatMoles, formatVolume } from '../quantityFormat';
import { remainingFractions, sizeForRemaining, realReactionSeconds, playbackMs, isSpedUp, formatRealDuration, reactionRunKey, PLAYBACK_CAP_SECONDS } from '../reactionPace';
import { bandFor } from '../temperature';
import type { Stoichiometry } from '@padcanvas/shared';
import { BenchBlock, type BlockState } from './BenchBlock';
import { LongPressGroup } from './LongPressGroup';
import { ContentsIcon, MenuIcon, ReactionIcon } from './BlockIcons';
import { VESSEL_2D, liquidTopYPx, rimYPx, PX_SCALE } from '../vessel2D';
import { VIEW_TILT } from '../projection';
import type { VesselType } from '../vesselTypes';
import type { StandType } from '../standTypes';
import type { BurnerType } from '../burnerTypes';
import { benchLayout, TRIPOD_RISE, type BenchPlacement } from '../benchLayout';
import { useLongPress } from '../useLongPress';

/** A finger rather than a mouse — the device has no right button to offer. */
function coarsePointer(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}
import { heatFraction, isBoilingNow } from '../temperature';
import {
  GLASS_TOP, GLASS_BOTTOM, GLASS_OPACITY, GLASS_LIP,
  GLASS_STROKE, GLASS_STROKE_WIDTH, GLASS_RIM_STROKE_WIDTH, GLASS_ETCH, lighten, darken, mixColors,
} from '../glassStyle';
import { Burner2D, RodStand2D, Tripod2D, TubeRack2D, LINE, type RodClamp, type Layer } from './Stands2D';
import { BenchTemperature, BENCH_TEMP_HEIGHT, BENCH_TEMP_OFFSET, BENCH_TEMP_LIFT } from './BenchTemperature';

interface ChemistryLabScene2DProps {
  containers: LabContainer[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  onSetTemperature: (index: number, celsius: number) => void;
  /** What has just been added, and to which vessel — the bench shows it going
   *  in. Null between pours; see ChemistryBench's own `pour`. */
  pour?: { index: number; substanceId: string; token: number; intoEmpty: boolean; ms: number } | null;
  /** The vessel being swirled right now, if any. The token restarts the
   *  animation when the same vessel is swirled twice running. */
  swirl?: { index: number; token: number } | null;
  /** Whether the contents blocks name amounts — the bench's own Show
   *  quantities. */
  showQuantities: boolean;
  /** How big the lab is drawn — see ChemistryBench's uiScale. The bench's
   *  layout is untouched by it (the panels taking more room is what makes the
   *  bench smaller); only its TEXT reads it. */
  uiScale: number;
  /** Right-click on the stand itself rather than the glassware standing in it. */
  onStandMenu: (columnBaseIndex: number, clientX: number, clientY: number) => void;
  onRequestMenu: (index: number, clientX: number, clientY: number) => void;
}

/** The clear space an item claims around itself, in SCENE units — half on each
 *  side, so two neighbours end up exactly this far apart and the arithmetic
 *  needs no separate gap term or (n-1) case. An item's FOOTPRINT is its width
 *  plus this, and a shelf is just the sum of footprints.
 *
 *  Scene units because it is a relationship between objects: it grows and
 *  shrinks with the glassware, unlike the frame margins, which are a share of
 *  the panel. See docs/FeatureDesigns/bench-layout-target.md. */
const ITEM_PAD = 96;
/** How far the bench's shading reaches down from the seam where its surface
 *  meets the wall behind it. */
const BENCH_SEAM_H = 14;
/** Where a pour starts: high enough above the mouth to read as coming from
 *  something held over it, low enough to stay inside the vessel's own slot. */
const POUR_ABOVE_RIM_PX = 62;
const POUR_STREAM_W = 7;
/** The travelling highlight's own height, clipped to the ribbon. */
const POUR_FLOW_H = 34;
/**
 * How long it takes to pour HALF A VESSEL, by default: three seconds from the
 * first of it leaving the bottle to the level settling.
 *
 * A reference, not a fixed length — see ChemistryBench's pourDurationFor. What
 * goes in is not always the same amount, and a fixed three seconds made 2 mL
 * of concentrated acid take exactly as long to arrive as filling a beaker,
 * which is the one thing pouring is not: how long it takes IS how much there
 * is of it.
 *
 * Slow for an interface and right for this — the level climbing is the thing
 * being shown, and a pour over in half a second is a vessel that was simply
 * full the next time you looked. It stays a parameter (ChemistryBench's
 * `pourMs`) because how long to spend is a judgement about the lesson: a
 * teacher demonstrating wants to watch it, and a student on their twentieth
 * reagent does not.
 */
export const DEFAULT_POUR_MS = 3000;
/** How long the stream takes to fall from the bottle to the bottom of the
 *  vessel. A FIXED time, not a share of the pour: how long someone stands
 *  there pouring has nothing to do with how fast the liquid falls, and tying
 *  the two together made the level start rising while the stream was still
 *  visibly on its way down. Only a pour too short to contain it scales it
 *  down. */
const POUR_FALL_MS = 320;
/** How long the last of the stream takes to fall in once the pouring stops. */
const POUR_TAIL_MS = 380;

/**
 * The three stages of a pour, from its length.
 *
 * ZERO is a supported answer and means exactly what it says: no fall, no rise,
 * no tail — the vessel is filled, the way it was before any of this existed.
 * Everything downstream reads these numbers rather than the constants, so
 * there is one path through the code and not a separate "animated" one.
 */
export function pourStages(pourMs: number): { fall: number; rise: number; tail: number; total: number } {
  const total = Math.max(0, pourMs);
  // The fall and the tail come OUT of the pour rather than being added to it:
  // `pourMs` is how long the whole thing takes, start to settled, which is the
  // number anyone setting it is actually thinking about. A pour too short to
  // hold them at their natural length gives them back proportionally.
  const fall = Math.min(POUR_FALL_MS, total * 0.11);
  const tail = Math.min(POUR_TAIL_MS, total * 0.13);
  return { fall, rise: Math.max(0, total - fall - tail), tail, total };
}

/**
 * Dropping, which is not pouring in miniature.
 *
 * A drop is a countable event: the whole reason an indicator is dosed in drops
 * is that two of them is a different thing from six, so they leave the dropper
 * one at a time, far enough apart to be counted, and the bead is seen to swell
 * and let go rather than a short ribbon appearing. Drawing it as a brief pour
 * is the failure mode this exists to avoid.
 */
const DROP_INTERVAL_MS = 460;
/** Tip to surface, the hang at the tip included. */
const DROP_FLIGHT_MS = 520;
/** The moment after the last drop lands, which the ring it leaves needs to
 *  finish spreading — without it the sequence ended on the last drop and the
 *  final ring was cut off half-drawn. */
const DROP_SETTLE_MS = 340;
/** A thick liquid hangs longer and falls slower — the only place `flow` shows
 *  (substanceCatalog's LiquidFlow). */
const THICK_DROP_SLOWDOWN = 1.35;
/** How far above the rim the dropper is held, and how big it is drawn. */
const DROPPER_TUBE_H = 40;
const DROPPER_BULB_R = 8.5;
/**
 * The angle it is held at, in degrees, leaning to the right.
 *
 * Nobody holds a dropper upright over a flask: the hand comes in from the
 * side, and a vertical pipette reads as a fixture bolted above the bench
 * rather than something someone is holding. The TIP stays on the axis and the
 * bulb swings out — the drop still falls straight down from the tip, because
 * gravity does not care how the glass is held.
 */
const DROPPER_TILT_DEG = 24;
/**
 * How far a bottle is tipped to pour, and where it rests either side of it.
 *
 * Past horizontal: a bottle tipped to 90° is a bottle about to stop pouring,
 * and every real pour goes further than that. It tips over as the stream
 * starts and comes back upright as it stops — the same two moments the stream
 * itself has (see pourStages), so the hand and the liquid agree.
 */
const POUR_BOTTLE_TIP_DEG = 118;
const POUR_BOTTLE_REST_DEG = 74;

/**
 * The bottle a pour comes OUT of.
 *
 * The stream used to begin in mid-air a little above the rim, which is the one
 * thing a stream cannot do: liquid comes from somewhere, and the somewhere is
 * as much a part of what a student is watching as the vessel it lands in. It
 * carries the reagent's own colour, so the bottle is also what says WHICH
 * reagent is going in — the same job the dropper's tube does.
 *
 * Drawn with its MOUTH at the origin and its body up and to the right, so the
 * caller places it at the head of the stream and the tipping is a rotation
 * about that same point: the neck stays where the liquid leaves, whatever
 * angle the bottle is held at.
 */
/*
 * How big the bottle is drawn. TWICE what it was at first: a reagent bottle
 * beside a flask is not a small thing, and at half this it read as a phial
 * being tipped rather than as one of the bottles the shelf is full of.
 *
 * Every measurement below is a multiple of this, so the size is one edit —
 * doubling the body alone gave a jar with a drinking straw for a neck.
 */
const BOTTLE_SCALE = 2;
const BOTTLE_W = 11.5 * BOTTLE_SCALE;
const BOTTLE_H = 44 * BOTTLE_SCALE;

function PourBottle({ substance, clear }: { substance: Substance; clear: boolean }): React.JSX.Element {
  const k = BOTTLE_SCALE;
  const W = BOTTLE_W, H = BOTTLE_H, NECK = 11 * k, NECK_W = 4.2 * k;
  const SHOULDER = 13 * k, SHOULDER_Q = 5 * k, BASE_R = 3 * k;
  return (
    <g>
      {/* body: shoulders swelling out of the neck, a flat base at the far end */}
      <path
        d={`M ${-NECK_W} ${-NECK} Q ${-W} ${-NECK - SHOULDER_Q} ${-W} ${-NECK - SHOULDER}`
          + ` L ${-W} ${-H + BASE_R} Q ${-W} ${-H} ${-W + BASE_R} ${-H}`
          + ` L ${W - BASE_R} ${-H} Q ${W} ${-H} ${W} ${-H + BASE_R}`
          + ` L ${W} ${-NECK - SHOULDER} Q ${W} ${-NECK - SHOULDER_Q} ${NECK_W} ${-NECK} Z`}
        fill="rgba(220,232,244,0.42)" stroke="#93a5b6" strokeWidth={0.9 * k}
      />
      {/* what is in it — held at the SHOULDER, since a bottle being poured is
          a bottle tipped up: the liquid gathers at the neck end. */}
      <path
        d={`M ${-NECK_W} ${-NECK} Q ${-W} ${-NECK - SHOULDER_Q} ${-W} ${-NECK - SHOULDER}`
          + ` L ${-W} ${-H + 16 * k} L ${W} ${-H + 16 * k}`
          + ` L ${W} ${-NECK - SHOULDER} Q ${W} ${-NECK - SHOULDER_Q} ${NECK_W} ${-NECK} Z`}
        fill={substance.color} fillOpacity={clear ? 0.45 : 0.9}
      />
      {/* neck and lip */}
      <rect
        x={-NECK_W} y={-NECK} width={NECK_W * 2} height={NECK}
        fill="rgba(220,232,244,0.5)" stroke="#93a5b6" strokeWidth={0.8 * k}
      />
      <rect
        x={-NECK_W - 1 * k} y={-2.6 * k} width={NECK_W * 2 + 2 * k} height={2.6 * k}
        rx={1 * k} fill="#8a95ab"
      />
      {/* a plain label patch: a bottle with no label is a jar */}
      <rect
        x={-W + 2.5 * k} y={-H + 7 * k} width={(W - 2.5 * k) * 2} height={12 * k}
        rx={1.5 * k} fill="#f2f4f7" opacity={0.82}
      />
      <rect x={-W + 5 * k} y={-H + 10.5 * k} width={(W - 5 * k) * 2} height={1.3 * k} rx={0.6 * k} fill="#9aa7b5" />
      <rect x={-W + 5 * k} y={-H + 13.5 * k} width={(W - 6 * k) * 1.6} height={1.3 * k} rx={0.6 * k} fill="#b6c0cb" />
      <ellipse cx={-W + 4 * k} cy={-H + 26 * k} rx={1.6 * k} ry={6 * k} fill="#fff" opacity={0.4} />
    </g>
  );
}

/**
 * The spatula a powder comes off.
 *
 * The third dispenser, and the same idea as the other two: a solid tipped in
 * came from somewhere, and the somewhere says which reagent and how it is
 * handled. A spoon is right for what is measured in GRAMS — powders and
 * crystals, a spatula-full at a time. It is deliberately NOT drawn for
 * anything counted in pieces: a nail, a strip of magnesium or three marble
 * chips are put in by hand or with forceps, and a spoonful of nails is a
 * picture of the wrong laboratory.
 *
 * Drawn with the bowl's LIP at the origin and the handle out to the right, so
 * the caller places it at the head of the fall and tipping is a rotation about
 * that same point — the powder leaves from where the lip is, whatever angle
 * the spoon is held at.
 */
const SPATULA_TIP_DEG = -58;
const SPATULA_REST_DEG = -8;
const SPATULA_BOWL_RX = 9, SPATULA_BOWL_RY = 4.4, SPATULA_HANDLE_L = 38;

function Spatula({ substance }: { substance: Substance }): React.JSX.Element {
  const BOWL_RX = SPATULA_BOWL_RX, BOWL_RY = SPATULA_BOWL_RY, HANDLE_L = SPATULA_HANDLE_L;
  return (
    <g>
      {/* handle: a long flat blade, as a laboratory spatula is */}
      <rect
        x={BOWL_RX * 0.7} y={-3.4} width={HANDLE_L} height={2.8} rx={1.4}
        fill="#c3ccd6" stroke="#8e9aa8" strokeWidth={0.7}
      />
      {/* the bowl, seen from a little above: an ellipse with a lip that meets
          the origin, and its own shadowed inside so it reads as a hollow */}
      <ellipse
        cx={-BOWL_RX * 0.55} cy={-2} rx={BOWL_RX} ry={BOWL_RY}
        fill="#d3dbe4" stroke="#8e9aa8" strokeWidth={0.8}
      />
      <ellipse cx={-BOWL_RX * 0.55} cy={-2.4} rx={BOWL_RX * 0.78} ry={BOWL_RY * 0.66} fill="#aeb9c6" />
      {/* what is in it — a small heap, in the reagent's own colour */}
      <path
        d={`M ${-BOWL_RX * 1.2} -2.6 Q ${-BOWL_RX * 0.55} ${-BOWL_RY - 3.4} ${BOWL_RX * 0.1} -2.6 Z`}
        fill={substance.color} stroke={darken(substance.color, 0.2)} strokeWidth={0.5}
      />
    </g>
  );
}

/** How long a run of `count` drops takes, from the first bead swelling to the
 *  last one landing. The bench asks, because how long a pour lasts is its
 *  decision — and a drop sequence's length is not a volume: it is however long
 *  it takes to let that many drops go. */
export function dropSequenceMs(substance: Substance, count: number): number {
  const slow = substance.flow === 'thick' ? THICK_DROP_SLOWDOWN : 1;
  return Math.max(1, count - 1) * DROP_INTERVAL_MS * slow + DROP_FLIGHT_MS * slow + DROP_SETTLE_MS;
}

/**
 * The dropper over the mouth, and the drops falling from it.
 *
 * Each drop is one element that swells at the tip, necks, lets go and falls —
 * all in one keyframe track (see .chemlab-drop), staggered by its index, so
 * the sequence needs no timers and no state: it is a function of which drop
 * this is. What it leaves on arrival is a RING, not a splash. Two drops
 * displace a twentieth of a millilitre; a splash would be a lie about how much
 * went in, and the ring is what the eye is actually shown in a real flask.
 */
function DropperDrops({ substance, count, fromY, toY, clear }: {
  substance: Substance;
  count: number;
  /** The dropper's tip. */
  fromY: number;
  /** The surface it lands on, or the floor of a dry vessel. */
  toY: number;
  /** Whether the liquid has no colour of its own — a colourless drop marks the
   *  surface without tinting it. */
  clear: boolean;
}): React.JSX.Element {
  const slow = substance.flow === 'thick' ? THICK_DROP_SLOWDOWN : 1;
  const interval = DROP_INTERVAL_MS * slow;
  const flight = DROP_FLIGHT_MS * slow;
  const r = 3.1 * (substance.flow === 'thick' ? 1.25 : 1);
  const fall = toY - fromY;
  return (
    <g>
      {/* The dropper itself: a bulb and a tapered glass tube, squeezing once
          per drop. Held over the mouth rather than dipped into it — a pipette
          touching the liquid is how you contaminate the bottle — and held at
          an angle, the way a hand holds one. Rotated about the TIP, so the
          glass leans away while the drops still leave from the axis they fall
          down. */}
      <g transform={`rotate(${DROPPER_TILT_DEG} 0 ${fromY})`}>
        <rect
          x={-1.7} y={fromY - DROPPER_TUBE_H} width={3.4} height={DROPPER_TUBE_H}
          rx={1.6} fill="rgba(214,230,243,0.7)" stroke="#aebece" strokeWidth={0.7}
        />
        <ellipse
          className="chemlab-dropper-bulb"
          cx={0} cy={fromY - DROPPER_TUBE_H - DROPPER_BULB_R + 1} rx={DROPPER_BULB_R} ry={DROPPER_BULB_R * 1.15}
          fill="rgba(190,206,220,0.85)" stroke="#93a5b6" strokeWidth={0.8}
          // Once per drop and then still. It used to squeeze forever, which
          // read as a hand going on pressing a bulb that had nothing left to
          // give — the squeeze is what CAUSES a drop, so there are exactly as
          // many of them as there are drops.
          style={{ animationDuration: `${interval}ms`, animationIterationCount: count }}
        />
        {/* What is left in the tube, in the reagent's own colour — the one part
            of the dropper that says WHICH reagent this is. */}
        <rect
          x={-1} y={fromY - DROPPER_TUBE_H + 6} width={2} height={DROPPER_TUBE_H - 6}
          rx={1} fill={substance.color} fillOpacity={clear ? 0.35 : 0.9}
        />
      </g>
      {Array.from({ length: count }, (_, i) => {
        const delay = i * interval;
        // Not dead on the axis: a dropper is held by a hand, and drops that
        // land on exactly the same point read as one drop repeated.
        const x = ((i % 3) - 1) * 1.6;
        return (
          <g key={i}>
            <ellipse
              className="chemlab-drop"
              cx={x} cy={toY} rx={r} ry={r * 1.2}
              fill={substance.color} fillOpacity={clear ? 0.55 : 0.95}
              stroke={clear ? 'rgba(255,255,255,0.55)' : 'none'} strokeWidth={clear ? 0.6 : 0}
              style={{
                ['--chemlab-drop-h' as string]: `${fall}px`,
                animationDelay: `${delay}ms`,
                animationDuration: `${flight}ms`,
              }}
            />
            {/* The mark it makes: a ring running out from where it went in.
                Tinted for a coloured reagent, because the first thing you see
                of methyl orange is orange spreading from the point of entry
                before the mixture evens out. */}
            <ellipse
              className="chemlab-drop-ring"
              cx={x} cy={toY} rx={r * 3.4} ry={r * 3.4 * VIEW_TILT}
              fill="none" stroke={clear ? 'rgba(255,255,255,0.75)' : substance.color}
              strokeWidth={1.2}
              style={{ animationDelay: `${delay + flight}ms` }}
            />
          </g>
        );
      })}
    </g>
  );
}

/** How a solid falls, which is not one motion for all of them. */
type SolidMotion = 'flutter' | 'grain' | 'dust' | 'heavy';

/** One piece of a solid at rest: where its CENTRE is, how big it is, how it is
 *  turned, and when it starts falling. The centre and not the corner, because
 *  a piece that lies down is rotated about it, and a corner then says nothing
 *  about where the shape actually ends up. */
interface SolidPiece {
  x: number;
  cy: number;
  w: number;
  h: number;
  rx: number;
  delay: number;
  spin: number;
}

/**
 * The pieces a solid arrives as, where each one comes to rest, and how it gets
 * there.
 *
 * Not one shape for all of them, and not one motion either. A magnesium ribbon
 * is light and broad and FLUTTERS down; marble chips are dense and drop
 * straight; powder drifts. The catalogue already carries the form (see
 * SolidForm) because the shelf draws it — this is the same fact, falling.
 *
 * They arrive as a HEAP: tipped off a spatula they come down in a narrow
 * column and pile up, highest in the middle. Spread evenly across the floor
 * they read as scattered by hand, which is not how anything is added to a
 * beaker.
 *
 * And a long piece LIES DOWN. A nail standing on its point in the bottom of a
 * flask is the one thing it is certainly not doing.
 *
 * Deterministic in a seed taken from the vessel and the substance — not from
 * the pour — so the arrangement the pieces fall INTO is the one they
 * afterwards lie in. Keyed on the pour, they landed in one scatter and were
 * replaced by another the moment the animation ended.
 */
function solidPieces(seed: number, form: SolidForm | undefined, spread: number, floorHalf: number): {
  motion: SolidMotion;
  pieces: SolidPiece[];
  /** Where a powder ends up: one mound, not a layer. Only for 'dust' — every
   *  other form is drawn as the pieces it is made of, because you can see
   *  them. See the resting render. */
  mound?: { halfWidth: number; height: number; crest: number };
} {
  // A small deterministic generator: the same seed gives the same heap.
  let state = Math.abs(seed) % 100000;
  const rnd = (): number => {
    state = (state * 1103515245 + 12345) % 2147483648;
    return state / 2147483648;
  };
  /** `n` pieces down a narrow column, and how high each ends up: the middle of
   *  a heap is its top, so a piece near the centre rests on the ones already
   *  there and one at the edge barely rises off the floor.
   *
   *  `spread` widens the pile and flattens it by as much — what falls into
   *  liquid does not stack where it landed. See the caller. */
  const heap = (n: number, share: number, peak: number) => {
    // `share` is a fraction of the vessel's own FLOOR, not a number of pixels:
    // fixed widths put the same little clump in the middle of a test tube and
    // of a flask. It is a SMALL fraction, though — what is tipped in through a
    // mouth lands in a pile, not in a layer across the bottom. Wide enough to
    // read as several pieces, narrow enough to read as one helping.
    const across = floorHalf * share * spread;
    return Array.from({ length: n }, () => {
      const x = (rnd() - 0.5) * 2 * across;
      return { x, rest: (peak / spread) * (1 - Math.abs(x) / across) * (0.55 + rnd() * 0.45) };
    });
  };
  /** On its side: turned across, and sitting on the floor on what is now its
   *  long edge — so its WIDTH is the height it takes up, not its length. */
  const lying = (p: { x: number; rest: number }, w: number, h: number, rx: number, delay: number): SolidPiece =>
    ({ x: p.x, cy: -(p.rest + w / 2), w, h, rx, delay, spin: 90 + (rnd() - 0.5) * 34 });
  const upright = (
    p: { x: number; rest: number }, w: number, h: number, rx: number, delay: number, spin: number,
  ): SolidPiece => ({ x: p.x, cy: -(p.rest + h / 2), w, h, rx, delay, spin });

  switch (form) {
    case 'strip':
      // A few SHORT pieces, not one long ribbon: magnesium is cut into
      // centimetre lengths before it goes anywhere near a vessel, and one
      // strip falling alone reads as a dropped object rather than a reagent
      // being added.
      return { motion: 'flutter', pieces: heap(6, 0.22, 3).map((p, i) => lying(p, 3.5, 13, 1.6, i * 0.07)) };
    case 'nail':
      // A couple, and small. One nail is a dropped object; a nail as long as
      // the vessel's floor is a crowbar.
      return { motion: 'heavy', pieces: heap(3, 0.18, 3).map((p, i) => lying(p, 3, 15, 1.4, i * 0.09)) };
    case 'lump':
      return {
        motion: 'heavy',
        pieces: heap(3, 0.22, 7).map((p, i) => upright(p, 9, 8, 2.5, i * 0.08, rnd() * 60 - 30)),
      };
    case 'granules':
    case 'crystal':
      return {
        motion: 'grain',
        // A spoonful, not a handful of stones: many small ones read as the
        // grains they are, where a few large ones read as gravel. The delays
        // are short enough that the whole spoonful is down inside a second
        // however many of them there are.
        pieces: heap(55, 0.34, 8).map((p, i) => upright(p, 3.6, 3.6, 1, i * 0.011, rnd() * 90)),
      };
    default: {
      // Powder, and anything with no form recorded: a fine column of it. The
      // finest grain and the most of it — powder is the one form where you
      // cannot pick out an individual piece, and a countable dozen specks
      // reads as coarse sand instead.
      const SHARE = 0.28, PEAK = 7;
      const specks = heap(45, SHARE, PEAK);
      // Where it PILES, once it is down. The same width and height the specks
      // fell into, so the mound is the shape they made rather than a second
      // idea of where the powder went — and its crest sits over the middle of
      // where they actually landed, which is what makes two helpings read as
      // two mounds instead of one symmetrical hill drawn twice.
      const across = floorHalf * SHARE * spread;
      const crest = specks.reduce((sum, p) => sum + p.x, 0) / specks.length;
      return {
        motion: 'dust',
        pieces: specks.map((p, i) => upright(p, 2.6, 2.6, 1.3, i * 0.008, 0)),
        mound: { halfWidth: across, height: PEAK / spread, crest },
      };
    }
  }
}

/**
 * The drops a landing stream throws up: where each starts, which way it goes,
 * and how long it takes.
 *
 * MANY and SMALL. A splash is spray, not a handful of blobs — a few large
 * drops read as something being thrown out of the vessel, where a dozen small
 * ones read as a surface being broken. They are fixed rather than random,
 * because regenerated per frame a splash shimmers.
 */
const SPLASH_DROPS = [
  { x: -3, dx: -15, r: 2, delay: 0, duration: 0.6 },
  { x: 3, dx: 16, r: 1.8, delay: 0.09, duration: 0.68 },
  { x: -1, dx: -9, r: 1.5, delay: 0.17, duration: 0.52 },
  { x: 2, dx: 11, r: 1.6, delay: 0.24, duration: 0.58 },
  { x: -4, dx: -19, r: 1.3, delay: 0.31, duration: 0.64 },
  { x: 4, dx: 20, r: 1.2, delay: 0.38, duration: 0.56 },
  { x: 0, dx: -5, r: 1.4, delay: 0.45, duration: 0.5 },
  { x: 1, dx: 7, r: 1.1, delay: 0.52, duration: 0.54 },
];

/**
 * Watery rather than syrupy — which is one fact about a liquid that the bench
 * draws twice.
 *
 * A solution is water with something dissolved in it, and so is water; what is
 * left is the neat end of the shelf, the concentrated acids, which are dense
 * and oily. That difference is visible in two ways and both of them matter
 * here: a watery liquid splashes far harder when it lands, and a syrupy one
 * shows LESS of the bench through it — not because it absorbs any light, but
 * because a dense, strongly refracting liquid reads as having body where water
 * reads as a window.
 */
function isWatery(substance: Substance): boolean {
  return substance.physicalState === 'aqueous' || substance.category === 'solvent';
}

/** A small stable hash, so a vessel's contents keep the same arrangement
 *  across renders without anything having to be stored. */
function seedOf(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** How long the slowest solid is in the air: the longest fall (a fluttering
 *  ribbon, 0.85s) plus the last piece's delay. What is falling is not yet in
 *  the vessel, and this is how long that stays true. */
const SOLID_FLIGHT_MS = 1150;

/** How long the arrival is louder than the pouring that follows it. */
const IMPACT_MS = 500;
/** How much of the pouring throws liquid back up, as a share of it. Past this
 *  the vessel has depth and the stream goes into it rather than onto it. */
const SPLASH_SHARE = 0.4;
/** The extra drops of that first moment: twice as many again, thrown wider,
 *  and gone by the time the steady spatter has found its rhythm. */
const IMPACT_DROPS = [
  { x: -4, dx: -26, r: 1.9, delay: 0, duration: 0.42 },
  { x: 4, dx: 27, r: 1.8, delay: 0.03, duration: 0.46 },
  { x: -2, dx: -18, r: 1.6, delay: 0.06, duration: 0.4 },
  { x: 2, dx: 19, r: 1.5, delay: 0.09, duration: 0.44 },
  { x: -5, dx: -13, r: 1.4, delay: 0.12, duration: 0.38 },
  { x: 5, dx: 14, r: 1.3, delay: 0.15, duration: 0.42 },
  { x: -1, dx: -8, r: 1.2, delay: 0.18, duration: 0.36 },
  { x: 1, dx: 9, r: 1.2, delay: 0.21, duration: 0.4 },
  { x: -3, dx: -22, r: 1.1, delay: 0.24, duration: 0.44 },
  { x: 3, dx: 23, r: 1.1, delay: 0.27, duration: 0.4 },
  { x: 0, dx: -4, r: 1, delay: 0.3, duration: 0.34 },
  { x: 0, dx: 5, r: 1, delay: 0.33, duration: 0.36 },
];

/** The fine spatter of the back half of a pour: a few small drops at the point
 *  of entry, and none of the reach the first drops have. */
const FINE_DROPS = [
  { x: -2, dx: -4, r: 1.5, delay: 0, duration: 0.42 },
  { x: 2, dx: 5, r: 1.2, delay: 0.16, duration: 0.38 },
  { x: 0, dx: 2, r: 1.3, delay: 0.29, duration: 0.34 },
];

/**
 * The air the stream carries down, coming back up through the liquid.
 *
 * Clustered around where the stream goes in, because that is where the air is
 * going down: a bubble surfacing at the far wall of the vessel has no reason to
 * be there. A few wander, which is what keeps a cluster from reading as a
 * fountain. Rings rather than discs — a bubble under liquid is a surface, not a
 * blob of paint — and they start BELOW the level and go at it, which is the
 * direction that says they are in the liquid rather than over it.
 */
const POUR_BUBBLES = [
  { x: -4, r: 2.4, rise: 26, delay: 0, duration: 0.5 },
  { x: 4, r: 3, rise: 32, delay: 0.07, duration: 0.58 },
  { x: -1, r: 1.8, rise: 20, delay: 0.13, duration: 0.44 },
  { x: 6, r: 2, rise: 24, delay: 0.19, duration: 0.5 },
  { x: -6, r: 1.6, rise: 18, delay: 0.24, duration: 0.42 },
  { x: 2, r: 2.6, rise: 29, delay: 0.3, duration: 0.54 },
  { x: -8, r: 1.4, rise: 15, delay: 0.36, duration: 0.4 },
  { x: 8, r: 1.7, rise: 21, delay: 0.42, duration: 0.46 },
  { x: 0, r: 2.2, rise: 34, delay: 0.48, duration: 0.6 },
];

/** The surface working where the stream goes in: rings running out from the
 *  point of entry, one after another, for as long as it is going in. Two, so
 *  there is always one on its way out while the next starts. */
const ENTRY_RINGS = [
  { delay: 0, duration: 0.85 },
  { delay: 0.42, duration: 0.85 },
];

/** The contents label sits to the left of the vessel; these are its box. */
const LABEL_WIDTH = 126;
const LABEL_GAP = 8;
const LABEL_MAX_HEIGHT = 72;
/** How close to the scene's edge a block may be pushed when there is not room
 *  for it in its natural place beside the vessel. Scene units, like the block
 *  widths it is compared against. */
const BLOCK_EDGE_PAD = 6;
/** The reaction card sits to the RIGHT of the vessel — the side the contents
 *  label never occupies — showing what the mixture is actually doing right
 *  now (the same headline the step log reports), so a student watching the
 *  bench sees it without opening a step's "More" detail. Wider than the
 *  contents label since a reaction line ("Exothermic Reaction") runs longer
 *  than a substance name, and wide enough that a 3-substance mix's equation
 *  (e.g. "FeSO4 + H2SO4 + 2 HNO3 -> Fe2(SO4)3 + 2 NO + 2 H2O") fits on one
 *  line rather than wrapping mid-formula. */
const REACTION_CARD_WIDTH = 290;
/** A folded block is its icon and nothing else — 20px of button plus room for
 *  the focus ring. The slot shrinks to this so the mark sits against the glass. */
const FOLDED_BLOCK_PX = 26;
/** Shared default, so an untouched vessel doesn't allocate a state object per render. */
const EMPTY_BLOCKS: { info: BlockState; reaction: BlockState } = { info: {}, reaction: {} };
// A foreignObject clips to its own box — it does not grow to fit content —
// so this has to be generous enough for a wrapped equation (now broken into
// per-term spans, see ChemEquation) plus several headline lines, rather than
// exactly what one line needs.
const REACTION_CARD_MAX_HEIGHT = 220;
/** The narrowest the reaction card will go to stay beside its vessel rather
 *  than over it. Below this it is too cramped to read, and the old behaviour —
 *  sliding over the glass — is the lesser evil. */
const REACTION_CARD_MIN_WIDTH = 170;
/*
 * Zoom is a function of space and content, never of how many neighbours a
 * vessel happens to have. The two bounds below are stated in terms someone can
 * check on screen rather than as multipliers, and between them the scale is
 * continuous — there is no step, and in particular no separate ceiling for a
 * sparse bench. A third container used to drop the ceiling from 2 to 1.2,
 * shrinking every vessel already on the bench by 40% the moment it arrived.
 * See docs/FeatureDesigns/bench-layout-target.md.
 */

/** Frame margins, as a share of the PANEL — they are about the frame, so they
 *  are already proportional and must not also scale with zoom. A margin that is
 *  both a percentage of the panel and scaled by zoom has no solution. */
const MARGIN_X = 0.10;
/**
 * Draws the layout's own boxes over the bench: the frame the margins leave, the
 * side gullies, each item's footprint and the padding inside it. Flip to `true`
 * while tuning the numbers in this file — every quantity in the zoom
 * calculation becomes something you can see rather than something you infer
 * from a screenshot. Ships `false`.
 */
const DEBUG_LAYOUT = false;
const MARGIN_BOTTOM = 0.15;

/** MIN_ZOOM by legibility: below this the narrowest vessel stops reading as
 *  glassware. Derived from that width rather than picked as a ratio.
 *
 *  It must stay below 1: the narrowest vessel is a test tube about 25px wide
 *  intrinsically, so asking for 34 on screen made MIN_ZOOM 1.344 — a FLOOR
 *  above the natural scale, which forced the bench wider than its panel and
 *  put a scrollbar under three flasks. */
const MIN_VESSEL_WIDTH_ON_SCREEN = 18;

/** However well they fit, a rack holds at most this many. Zoom answers "can
 *  these be read?", which a wide panel can keep answering yes to long past the
 *  point a row of seven reads as a shelf rather than a queue. */
const MAX_PER_RACK = 5;

/*
 * Label text is drawn inside the SVG, so it scales with the bench: a 10px
 * label would render at 14px on an empty bench and at 7px on a full one. Type
 * does not work that way, and the bench's zoom is no business of its type —
 * that zoom answers "how many vessels are out and how much room is left",
 * which has nothing to say about how big writing needs to be to be read.
 *
 * So the on-screen size is FIXED, and the multiplier that achieves it is
 * handed to CSS as a variable: divide the target by the zoom, and the zoom
 * multiplies it straight back out. Adding a fourth vessel no longer shrinks
 * the writing. How big that target is comes from the lab's own scale
 * (--chemlab-ui-scale, see ChemistryBench) — one setting for the whole room.
 *
 * Every label keeps its size relative to the others; only the pair as a whole
 * is pinned.
 */
const LABEL_BASE_PX = 10;
/** What a label is, on the glass, at scale 1. Was capped at 15 while the
 *  bench zoomed past it, which is exactly how a demo on a big screen ended up
 *  with enormous flasks and unreadable writing. */
const LABEL_TARGET_PX = 15;
const NARROWEST_VESSEL_PX = Math.min(...Object.values(VESSEL_2D).map(v => v.maxHalfWidthPx * 2));
const MIN_ZOOM = MIN_VESSEL_WIDTH_ON_SCREEN / NARROWEST_VESSEL_PX;

/** MAX_ZOOM by absolute size: one flask on a wide bench should look generous,
 *  not absurd. Expressed as a share of bench height so it holds at any panel
 *  size; computed per render, since it depends on the box. */
const MAX_VESSEL_SHARE_OF_HEIGHT = 0.40;
const TALLEST_VESSEL_PX = Math.max(...Object.values(VESSEL_2D).map(v => v.overallHeightPx));
/** What the scene assumes for one frame if it is asked to lay out before the
 *  panel has been measured. Not a design number and nothing depends on it —
 *  the box is measured synchronously on mount, so this is at most one paint. */
const UNMEASURED_FRAME = 560;

/** Room under a bench line — just a little breathing space now that the
 *  temperature scale stands beside the apparatus rather than below it. */
const BELOW_BENCH = 20;
const TOP_PADDING = 18;
/** How far a delivery tube rises above the mouths it connects. */
const TUBE_MIN_LIFT = 74;
const TUBE_FILL = '#93a7bd';
const LABEL_HEADROOM = 18;
const BUBBLE_COUNT = 7;
/**
 * What each rate of gas evolution looks like. `steady` is the bench's old
 * behaviour exactly, so nothing that came back before the field existed
 * changes; `vigorous` is three times the bubbles, faster and larger, which is
 * what "effervesces vigorously" means to anyone who has watched it.
 */
const FIZZ: Record<'slow' | 'steady' | 'vigorous', {
  count: number; duration: number; spread: number; radius: number;
}> = {
  slow: { count: 3, duration: 2.1, spread: 2.6, radius: 2.1 },
  steady: { count: BUBBLE_COUNT, duration: 1.2, spread: 1.6, radius: 2.6 },
  vigorous: { count: 20, duration: 0.72, spread: 0.7, radius: 3.2 },
};
const SMOKE_COUNT = 5;
const FUMES_COUNT = 8;
/** Static decorative bubbles suspended in any liquid — what makes a flat fill
 *  read as a LIQUID rather than a coloured block. Distinct from the animated
 *  bubbles, which mean gas is actually coming off. */
const STILL_BUBBLE_COUNT = 7;
const BOIL_BUBBLE_COUNT = 8;
const STEAM_COUNT = 5;

/**
 * The details that belong to one particular piece of glassware rather than to
 * the shared profile — a beaker's pouring spout and graduation marks. Drawn
 * over the glass but under the outline, so they read as part of the vessel.
 */
function VesselDetail({ vesselType, art, rimY }: {
  vesselType: VesselType; art: (typeof VESSEL_2D)[VesselType]; rimY: number;
}): React.JSX.Element {
  return (
    <g>
      {/* The ground-glass collar just below the mouth — the single detail that
          most says "laboratory glassware" rather than "a shape". */}
      {art.collar && (
        <>
          <path
            d={art.collar.bandPath} className="chemlab-glass-lip"
            fill={GLASS_LIP} stroke={GLASS_STROKE} strokeWidth={GLASS_RIM_STROKE_WIDTH} strokeLinejoin="round"
          />
          {/* The front edge of its top face — what stops a short cylinder from
              reading as a flat tab stuck on the neck. */}
          <path d={art.collar.topFrontArc} fill="none" stroke={GLASS_STROKE} strokeWidth={GLASS_RIM_STROKE_WIDTH} />
        </>
      )}

      {/* The pouring spout is NOT drawn here. It used to be a separate wedge
          laid over the mouth, which is what it always looked like; it is a
          bulge in the rim's own curve now (vesselGeometry.ts's Spout), so it
          arrives with the rim band rather than on top of it. */}
      {vesselType === 'beaker' && (
        <>
          {/* Graduations, etched down the right. Short ticks close to the
              wall, alternating long and short: they are a scale on the glass,
              not lines drawn across the contents. Each is an ARC, not a
              segment — a graduation runs round the glass, so from above it
              curves exactly as the liquid's surface below it does, and a
              straight tick is the one thing that gives a tilted cylinder
              away. */}
          <g stroke={GLASS_ETCH} strokeWidth={2.2} strokeLinecap="round" fill="none">
            {[0.3, 0.42, 0.54, 0.66, 0.78].map((t, i) => {
              const r = art.rimHalfWidthPx, y = rimY * t;
              // Both ends sit on the near half of the circle at this height,
              // dropping below its centre by however far round they are.
              const drop = (x: number) => y + Math.sqrt(Math.max(0, 1 - (x / r) ** 2)) * r * VIEW_TILT;
              const from = r * (i % 2 === 0 ? 0.34 : 0.56), to = r * 0.88;
              return <path key={t} d={`M ${from.toFixed(1)},${drop(from).toFixed(1)} A ${r.toFixed(1)},${(r * VIEW_TILT).toFixed(1)} 0 0 0 ${to.toFixed(1)},${drop(to).toFixed(1)}`} />;
            })}
          </g>
        </>
      )}
    </g>
  );
}

/** Strips a trailing "with ... fumes/gas/smoke" clause some colorChange
 *  descriptions fold in — the LLM sometimes conflates the escaping fumes'
 *  colour into this SAME string ("colorless with yellowish-green fumes")
 *  instead of using the dedicated gasEvolution.color field that already
 *  exists for it, and colorDescriptionToHex's keyword matching then picks
 *  up "yellow" from the fumes clause and paints the whole LIQUID solid
 *  yellow even though the sentence itself says "colorless". */
function withoutFumesClause(description: string): string {
  return description.replace(/\s+with\s+.*\b(fumes?|gas|smoke)\b.*$/i, '');
}

/** What colour a given mix is drawn.
 *
 *  `precipitateSettledFraction` (0-1, default 0) is how much of a forming
 *  precipitate has settled out so far — see its own definition below. A
 *  precipitate is solid actively leaving solution, so the liquid isn't a
 *  static fill in that case: it fades from its pre-reaction colour
 *  (`from`) toward the model's stated RESIDUAL colour (`to`) as the solid
 *  settles, the same way a real solution pales as the coloured species
 *  that was in it is carried out by the solid — colorChange.to here means
 *  "what's left dissolved once the precipitate has taken its colour with
 *  it" (which a heavy, near-complete precipitation correctly fades toward
 *  colorless), a distinct fact from precipitate.color (what the SOLID
 *  itself looks like). Callers with no settling to animate (the 3D view
 *  has no equivalent timer) simply get the pre-reaction colour throughout,
 *  same as before this existed. */
function liquidColor(
  substances: Substance[],
  outcome: ChemistryLabOutcome | undefined,
  precipitateSettledFraction = 0,
): string {
  if (outcome?.colorChange) {
    const hasPrecipitate = !!outcome.precipitate && outcome.precipitate.amount !== 'none';
    if (!hasPrecipitate) {
      const { to, toHex } = outcome.colorChange;
      const cleaned = withoutFumesClause(to);
      return resolveColorHex(toHex, cleaned || to);
    }
    const { from, fromHex, to, toHex } = outcome.colorChange;
    const fromResolved = resolveColorHex(fromHex, from);
    const toCleaned = withoutFumesClause(to);
    const toResolved = resolveColorHex(toHex, toCleaned || to);
    return mixColors(fromResolved, toResolved, precipitateSettledFraction);
  }
  if (substances.length === 0) return '#c7ccd6';
  /*
   * Only what is IN SOLUTION tints the liquid.
   *
   * Averaging everything in the vessel meant a solid lying on the floor
   * coloured the liquid above it: gold foil under concentrated sulphuric acid
   * turned the whole flask gold, while the analysis printed "no visible
   * change" two lines away. A solid that genuinely dissolves says so through
   * the outcome's colorChange, which is handled above and wins.
   *
   * A vessel holding nothing but solids has no liquid to draw at all — the
   * fallback is only there for the colour of the heap.
   */
  const inSolution = substances.filter(sub => sub.physicalState !== 'solid');
  const tinting = inSolution.length > 0 ? inSolution : substances;
  const [r, g, b] = tinting.reduce(
    (acc, s) => {
      const hex = s.color.replace('#', '');
      const num = parseInt(hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex, 16);
      return [acc[0] + ((num >> 16) & 255), acc[1] + ((num >> 8) & 255), acc[2] + (num & 255)];
    },
    [0, 0, 0],
  ).map(v => Math.round(v / tinting.length));
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

/** A genuinely white precipitate (AgCl, Pb(OH)2, ...) resolves to a very pale
 *  hex (see colorMapping's white/cloudy/colorless fallback) — distinct from a
 *  merely light TINT like pale green, which still has a dominant channel. */
function isNearWhite(hex: string): boolean {
  const raw = hex.replace('#', '');
  const num = parseInt(raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw, 16);
  const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  return Math.min(r, g, b) >= 220;
}

/**
 * Everything the column's stand draws — rendered ONCE per column, not per
 * container, so a shared rod is one rod. Burners are per container (each has
 * its own) but only the ones benchLayout says a flame can actually reach.
 */
function ColumnStand({ standType, members, layer }: {
  standType: StandType;
  members: { art: (typeof VESSEL_2D)[VesselType]; placement: BenchPlacement; burnerType: BurnerType; heat: number }[];
  layer: Layer;
}): React.JSX.Element | null {
  const base = members[0];
  const { placement } = base;

  // No stand at all — a flat-bottomed vessel standing on the bench. (A burner
  // can't be here: lighting one stands the vessel on a tripod first.)
  if (standType === 'none') return null;

  if (standType === 'tube-rack') return <TubeRack2D layer={layer} />;

  if (standType === 'tripod') {
    // The gauze sits at a fixed height and the vessel sits ON it — always, lit
    // or not. That is what leaves room for the burner to be seen underneath
    // instead of hiding behind the glassware.
    const ringY = -TRIPOD_RISE * PX_SCALE;
    // A round bottom is cradled by the ring itself; only a flat base gets a
    // gauze to stand on. See Tripod2D.
    const cradle = !base.art.hasFlatBase;
    if (layer === 'front') {
      return (
        <g>
          <Burner2D type={base.burnerType} flameY={ringY} heat={base.heat} layer="front" />
          <Tripod2D ringY={ringY} ringRadiusPx={base.art.standRadiusPx} cradle={cradle} layer="front" />
        </g>
      );
    }
    return (
      <g>
        {/* The burner FIRST: it stands under the tripod, so the gauze is drawn
            over its flame rather than the other way round. The gauze is woven
            wire and painted slightly transparent, so the flame still reads
            through it — which is what a flame under a gauze looks like. */}
        <Burner2D type={base.burnerType} flameY={ringY} heat={base.heat} layer="behind" />
        <Tripod2D ringY={ringY} ringRadiusPx={base.art.standRadiusPx} cradle={cradle} layer="behind" />
      </g>
    );
  }

  // Rod stands (retort / multi-level): one base + rod, a clamp per vessel.
  const clamps: RodClamp[] = members.map(m => ({
    y: -m.placement.rise * PX_SCALE + m.art.gripYPx,
    gripHalfWidthPx: m.art.gripHalfWidthPx,
  }));
  return (
    <g>
      <RodStand2D
        rodXPx={placement.rodX * PX_SCALE} rodTopPx={placement.rodTop * PX_SCALE}
        clamps={clamps} layer={layer}
      />
      {/* A burner stands on the bench, so it can only ever reach the LOWEST
          vessel in the column — benchLayout says as much via canHeat. */}
      <Burner2D type={base.burnerType} flameY={-base.placement.rise * PX_SCALE} heat={base.heat} layer={layer} />
    </g>
  );
}

/**
 * The delivery tubes — a length of rubber tubing arcing from one vessel's
 * mouth into another's, carrying whatever gas the first gives off. Drawn
 * across the whole bench rather than inside a column, since the two ends can
 * be on different shelves entirely.
 */
function DeliveryTubes({ containers, rims }: {
  containers: LabContainer[];
  rims: Map<string, { x: number; y: number }>;
}): React.JSX.Element {
  return (
    <g>
      {containers.map(c => {
        const from = rims.get(c.id);
        const to = c.pipeToId ? rims.get(c.pipeToId) : undefined;
        if (!from || !to) return null;
        // Lifts clear of both mouths before crossing, so the tube arcs over the
        // bench rather than cutting through whatever stands between them.
        const lift = Math.max(TUBE_MIN_LIFT, Math.abs(to.x - from.x) * 0.32);
        const d = `M ${from.x},${from.y - 2} C ${from.x},${from.y - lift} ${to.x},${to.y - lift} ${to.x},${to.y - 2}`;
        return (
          <g key={c.id}>
            <path d={d} fill="none" stroke={GLASS_STROKE} strokeWidth={9} strokeLinecap="round" />
            <path d={d} fill="none" stroke={TUBE_FILL} strokeWidth={5.5} strokeLinecap="round" />
            {/* Bungs — the tube doesn't just touch the glass, it's stoppered in. */}
            {[from, to].map((end, i) => (
              <rect
                key={i} x={end.x - 9} y={end.y - 5} width={18} height={10} rx={3}
                fill={TUBE_FILL} stroke={GLASS_STROKE} strokeWidth={1.8}
              />
            ))}
          </g>
        );
      })}
    </g>
  );
}

interface VesselProps {
  containerId: string;
  vesselType: VesselType;
  risePx: number;
  offsetXPx: number;
  hasGlassRod: boolean;
  glassRodCoating?: string | null;
  /** What the vessel holds, with how much of each — see labState's
   *  VesselContent. Resolved to substances inside, since almost everything
   *  drawn wants the substance and only the solids want the amount. */
  contents: VesselContent[];
  outcome?: ChemistryLabOutcome;
  /** The burner setting `outcome` was computed at — passed to headlineItems
   *  so the reaction card can say "Boiling" alongside whatever the mix
   *  itself did (see outcomeDisplay.ts). */
  celsius: number;
  isBoiling: boolean;
  /** A delivery tube is connected to this vessel, at EITHER end — gas is being
   *  routed rather than escaping into the open air, so the rim-level
   *  smoke/fume puffs (which read as "gas is escaping here") would visually
   *  contradict that and are suppressed.
   *
   *  Both ends, not just the source: a vessel receiving gas has the tube in
   *  its mouth too, and a plume rising from a tube that is visibly plumbed
   *  into another one reads as a leak. The lime water in the carbon dioxide
   *  experiment is exothermic, so it puffed away at the receiving end while
   *  the delivery tube arced into it. */
  hasPipe: boolean;
  isSelected: boolean;
  /** Set for the moment after something is added to THIS vessel: what it was,
   *  a token that changes per pour so the animation restarts, and whether the
   *  vessel was dry — which decides whether a level rises or a surface is just
   *  disturbed. */
  pour?: { substanceId: string; token: number; intoEmpty: boolean; ms: number };
  /** Whether the contents block names how much of each reagent is in the
   *  vessel. Off by default: the amounts are always there, but a bench that
   *  states them on every chip is a bench about measuring rather than about
   *  what happens. */
  showQuantities: boolean;
  /** The quantitative bench's working for this vessel's latest reaction, when
   *  there is one — what the two blocks beside it state in numbers. Absent on
   *  the free bench, which is what keeps both blocks exactly as they were. */
  working?: Stoichiometry;
  /** What the blocks beside this vessel are scaled by — their type and their
   *  slots together. See the scene's own textScale. */
  textScale: number;
  /** Non-null while this vessel is being swirled; the number restarts the
   *  animation. */
  swirlToken?: number | null;
  /** How many times it has been swirled — see LabContainer.swirls. It seeds
   *  the solids' scatter, so what has not dissolved comes to rest somewhere
   *  new every time the vessel is moved. */
  swirls: number;
  /** How long that pour lasts; 0 for none at all. */
  /** Where this vessel's column stands in the scene, and how wide the scene
   *  is — the two numbers the overlay blocks need to keep themselves inside
   *  the picture. Everything else here is drawn relative to the vessel and
   *  never has to know. */
  columnXPx: number;
  sceneWidthPx: number;
  /**
   * View state for this vessel's two blocks. Held by the scene, not by the
   * container: whether a box is folded and where it has been dragged is a
   * property of the bench being looked at, never of the chemistry in the glass.
   */
  blocks: { info: BlockState; reaction: BlockState };
  onBlockChange: (kind: 'info' | 'reaction', next: BlockState) => void;
  /** The scene's scale, so a drag in screen pixels lands in scene units. */
  zoom: number;
  loading: boolean;
  phPaperColor?: string | null;
  onClick: () => void;
  onRequestMenu: (clientX: number, clientY: number) => void;
  /**
   * Which pass this is. 'body' draws the glassware and everything in it;
   * 'overlay' draws only the cards beside it, in a layer rendered after every
   * vessel on the bench — SVG has no z-index, so being later in the document
   * is the only way to be on top.
   */
  layer?: 'body' | 'overlay';
}

/**
 * Where the latest reaction in a vessel is in its run: fizzing, tapering off,
 * or over. A new run starts when the working says more product was made (see
 * reactionRunKey); anything else — a dip, a swirl, a reload of a bench whose
 * reaction finished long ago — leaves the phase where it is, so the bench never
 * replays a reaction that is not happening.
 */
type RunPhase = 'running' | 'fading' | 'done';

function useReactionRun(runKey: string, ms: number): RunPhase {
  const seen = useRef(runKey);
  const [phase, setPhase] = useState<RunPhase>('done');
  useEffect(() => {
    if (runKey === seen.current) return;
    seen.current = runKey;
    if (!runKey || ms <= 0) { setPhase('done'); return; }
    setPhase('running');
    // The gas tapers over the last stretch rather than stopping dead: the
    // rate falls as the reagents run down, and the last bubbles are sparse.
    const fade = window.setTimeout(() => setPhase('fading'), ms * 0.6);
    const done = window.setTimeout(() => setPhase('done'), ms);
    return () => { window.clearTimeout(fade); window.clearTimeout(done); };
  }, [runKey, ms]);
  return phase;
}

/** How many helpings of one solid are ever drawn. See restingSolids. */
const MAX_HELPINGS_DRAWN = 8;

function VesselIllustration({
  containerId, vesselType, risePx, offsetXPx, hasGlassRod, glassRodCoating, contents, outcome, celsius,
  isBoiling, hasPipe, isSelected, pour, textScale, showQuantities, working, swirlToken, swirls, columnXPx, sceneWidthPx, blocks, onBlockChange, zoom, loading, phPaperColor,
  onClick, onRequestMenu,
  layer = 'body',
}: VesselProps): React.JSX.Element {
  const art = VESSEL_2D[vesselType];
  const substances = useMemo(() => heldSubstances(contents), [contents]);
  // A vessel holding only dry solids (sulphur alone, or two powders tipped in
  // together with no water) has nothing to draw as a liquid — a surface line
  // and suspended bubbles on a beaker of dry sodium chloride would show a
  // liquid that isn't there. Rendered as a dry pile instead; see below.
  const hasLiquid = substances.some(s => s.physicalState === 'liquid' || s.physicalState === 'aqueous');
  // Right-click, and the finger's version of it. Declared up here because the
  // overlay pass returns before the glassware is drawn, and a hook cannot be
  // called after that.
  const longPress = useLongPress(onRequestMenu);

  const added = pour ? findSubstance(pour.substanceId) : undefined;
  /** Only something that pours gets a stream; a solid falls in pieces. */
  /** Dispensed a drop at a time — see substanceCatalog's Dose. Split out of
   *  `pouring` because it is a different act, not a smaller one. */
  const dropping = added?.dose?.unit === 'drop' ? added : undefined;
  const pouring = added && !dropping && added.physicalState !== 'solid' ? added : undefined;
  /** How hard the pour splashes: water throws far more up than syrup does.
   *  See isWatery. */
  const splashScale = pouring && isWatery(pouring) ? 2 : 1;

  /*
   * The level rising, as a fraction of where it will come to rest.
   *
   * Driven frame by frame rather than by CSS, because the liquid is a SHAPE
   * cut to the vessel at whatever height it stands: `liquidPath` takes the
   * fraction and returns the body, the surface ellipse and the arc across the
   * front of it, all correct for that level. Sliding the finished shape up
   * from below — which is what a CSS transform can do — showed a cone growing
   * inside the glass instead, narrower than the vessel it was supposedly
   * filling, because the shape being moved was the one for the FINAL height.
   *
   * null whenever nothing is filling, which is every frame but these: the
   * vessel then draws its resting level and no work is done.
   */
  const stages = pourStages(pour?.ms ?? 0);
  const [risen, setRisen] = useState<number | null>(null);
  /**
   * What the stream is doing to this vessel, which changes as it fills.
   *
   * 'splash' — it is hitting a hard floor, or a shallow puddle, and throwing
   *   liquid back up. This is the first stretch of the pour only: once there
   *   is real depth in the vessel the stream is going INTO something rather
   *   than onto it, and drops still flying at that point are the giveaway
   *   that nothing is modelling the difference.
   * 'bubbles' — the rest of it. The stream carries air down with it, and that
   *   air comes back up through the liquid instead.
   *
   * Its own state rather than a reading of `risen`, because a pour into a
   * vessel that already holds something moves no level at all and still
   * disturbs the surface it lands on.
   */
  const [phase, setPhase] = useState<'none' | 'splash' | 'bubbles'>('none');
  /** The first moment of 'splash', which is louder than the rest of it. */
  const [impact, setImpact] = useState(false);
  /**
   * True while a solid is still on its way down.
   *
   * A dropped reagent used to be IN the vessel from the moment it was clicked
   * — the heap on the floor was drawn while the pieces were still visibly in
   * the air above it, so the gold arrived twice. What is falling is not yet in
   * the vessel; this is how the drawing knows.
   */
  const [solidInFlight, setSolidInFlight] = useState(false);
  const landing = phase !== 'none';
  /**
   * How the level moves, across both of the stages that put liquid in.
   *
   * STEADILY while the stream is running: a vessel fills at the rate it is
   * poured into, so the level climbs at a constant rate rather than easing
   * off. An ease-out here — the reflex for anything that moves — put the
   * liquid at three quarters of its level in the first fifth of the pour and
   * left the stream running for seconds over a level that was no longer
   * moving.
   *
   * And it KEEPS climbing while the last of the stream falls in, because that
   * liquid goes into the vessel too. Slowing as it goes, since what is
   * arriving is the tail of the ribbon and there is less of it every moment —
   * the level reaching its mark is the pour being over.
   *
   * Then one rock, damped to nothing: liquid does not stop dead at its level.
   */
  const easeRise = (p: number): number => {
    // Where the stream stops and the tail begins, as a share of the whole.
    const TAIL_FROM = stages.tail > 0 ? stages.rise / (stages.rise + stages.tail) : 1;
    // What the steady part of the pour delivers. The rest is the tail's.
    const POURED = 0.93;
    if (p <= TAIL_FROM) return (p / TAIL_FROM) * POURED;
    const q = Math.min(1, (p - TAIL_FROM) / (1 - TAIL_FROM));
    // Ease-out for the tail, then the settle rides on top of the last of it.
    const arrived = POURED + (1 - POURED) * (1 - Math.pow(1 - q, 2));
    return arrived + Math.sin(q * Math.PI) * 0.012 * q;
  };
  // Nothing to animate when the pour is instant: the vessel is simply filled.
  const filling = !!pouring && !!pour?.intoEmpty && stages.rise > 0;
  const isPouring = !!pouring && stages.total > 0;
  const droppingSolid = !!added && !pouring && !dropping && stages.total > 0;
  const pourToken = pour?.token;
  useEffect(() => {
    if (!droppingSolid) { setSolidInFlight(false); return; }
    setSolidInFlight(true);
    const landed = window.setTimeout(() => setSolidInFlight(false), stages.fall + SOLID_FLIGHT_MS);
    return () => window.clearTimeout(landed);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fixed per drop.
  }, [droppingSolid, pourToken]);
  useEffect(() => {
    if (!isPouring) { setPhase('none'); setImpact(false); return; }
    const starts = window.setTimeout(() => { setPhase('splash'); setImpact(true); }, stages.fall);
    const calms = window.setTimeout(() => setImpact(false), stages.fall + IMPACT_MS);
    // The splash is the first SPLASH_SHARE of the pouring and no more.
    const settles = window.setTimeout(
      () => { setPhase('bubbles'); setImpact(false); },
      stages.fall + stages.rise * SPLASH_SHARE,
    );
    // Off part-way through the tail: the last of the ribbon is thin, and a
    // surface still being worked by a stream that has visibly stopped is worse
    // than one that settles a moment early.
    const ends = window.setTimeout(() => setPhase('none'), stages.fall + stages.rise + stages.tail * 0.4);
    return () => {
      window.clearTimeout(starts);
      window.clearTimeout(calms);
      window.clearTimeout(settles);
      window.clearTimeout(ends);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the stages are
    // fixed for the life of a pour.
  }, [isPouring, pourToken]);
  useEffect(() => {
    if (!filling) { setRisen(null); return; }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const p = (now - start - stages.fall) / (stages.rise + stages.tail);
      if (p >= 1) { setRisen(null); return; }
      setRisen(p <= 0 ? 0 : p);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    /* A backgrounded tab runs no frames at all, and a vessel left mid-fill
       because someone switched away is a vessel that looks empty. The clock
       still runs there, so this ends the fill whether or not it was watched. */
    const done = window.setTimeout(() => setRisen(null), stages.total + 80);
    return () => { cancelAnimationFrame(frame); window.clearTimeout(done); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the stages are
    // fixed for the life of a pour; re-reading them mid-fill would restart it.
  }, [filling, pourToken]);

  /*
   * The contents caption, shrunk to the lines it actually drew.
   *
   * `width: fit-content` means "what the text wants, or what is available,
   * whichever is smaller" — so the moment a mixture name is longer than the
   * 126-unit slot, the box takes the whole slot and wraps inside it. The
   * longest line it drew may be 90 units, and nothing in CSS shrinks the box
   * back to it: there is no "be as wide as your widest wrapped line". So it is
   * measured here and applied.
   *
   * Greedy line-breaking makes that a fixed point: at exactly the widest
   * line's width every existing line still fits and the word that overflowed
   * still overflows, so the breaks do not move and the measurement does not
   * chase itself. The key is what guards the other direction — a longer name
   * must be measured against the FULL slot, not against the narrow box the
   * previous name left behind, so the width is dropped until this text has
   * been measured on its own.
   */
  const labelRef = useRef<HTMLDivElement>(null);
  const labelTextRef = useRef<HTMLSpanElement>(null);
  /* The slots the blocks are drawn in. Scene units like everything else, but
     multiplied by textScale so a card and the words in it grow together. */
  const labelW = LABEL_WIDTH * textScale;
  const labelH = LABEL_MAX_HEIGHT * textScale;
  const labelGap = LABEL_GAP * textScale;
  const foldedPx = FOLDED_BLOCK_PX * textScale;
  const reactionW = REACTION_CARD_WIDTH * textScale;
  const reactionH = REACTION_CARD_MAX_HEIGHT * textScale;
  const labelKey = `${substances.map(s => s.id).join('+')}|${loading}|${Math.round(zoom * 100)}|${Math.round(textScale * 100)}|${showQuantities}`;
  const [measured, setMeasured] = useState<{ key: string; width: number; lines: number } | null>(null);
  useLayoutEffect(() => {
    const box = labelRef.current;
    const text = labelTextRef.current;
    if (!box || !text) return;
    const range = document.createRange();
    range.selectNodeContents(text);
    const rects = Array.from(range.getClientRects()).filter(r => r.width > 0);
    if (rects.length === 0) return;
    // getClientRects is in screen pixels; everything inside the foreignObject
    // is in scene units, which the scene's own scale separates.
    const style = getComputedStyle(box);
    const widest = Math.max(...rects.map(r => r.width)) / (zoom || 1);
    const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    /* A line's rect is the words it drew; the break that produced it also
       needed room for the space that followed them, which the rect does not
       include. Half the type size covers a space with a little to spare —
       measured, this was 2.6 units on a 8.9-unit font. A caption that never
       wrapped needs none of it: nothing broke, so nothing is owed a space. */
    const slack = rects.length > 1 ? parseFloat(style.fontSize) * 0.5 : 0;

    if (measured?.key !== labelKey) {
      // First look, taken at the full slot: THIS is the wrap being kept.
      setMeasured({
        key: labelKey,
        lines: rects.length,
        width: Math.min(labelW, Math.ceil(widest + padding + slack)),
      });
      return;
    }
    // And the check that it was kept. Text measurement is not exact enough to
    // trust blind: if the narrower box broke a line that the slot did not,
    // give it back the room a line at a time rather than leaving a caption
    // wrapped worse than it started.
    if (rects.length > measured.lines && measured.width < labelW) {
      setMeasured({ ...measured, width: Math.min(labelW, Math.ceil(measured.width + slack)) });
    }
  });
  const labelWidth = measured?.key === labelKey ? measured.width : undefined;
  /**
   * The solids at rest in the vessel, one entry per HELPING — a second spatula
   * of the same reagent is its own scatter beside the first, which is why the
   * quantity is expanded here rather than drawn as one bigger heap.
   *
   * They lie on the floor whether or not there is liquid over them — a gold
   * strip dropped into water is still in the vessel, and used to be drawn
   * nowhere at all. What is IN the vessel is not everything it has been given:
   * the helping still falling is drawn falling, and joins this list when it
   * lands, in the scatter its seed already decided.
   */
  const restingSolids = useMemo(() => {
    const out: { sub: Substance; helping: number }[] = [];
    for (const held of contents) {
      const sub = findSubstance(held.substanceId);
      if (!sub || sub.physicalState !== 'solid') continue;
      const inFlight = solidInFlight && pour?.substanceId === sub.id ? 1 : 0;
      // Clamped: past a handful of scatters the floor is covered and another
      // one changes nothing anybody can see, while every one of them is real
      // SVG being redrawn each frame.
      const atRest = Math.min(MAX_HELPINGS_DRAWN, helpingsOf(sub, held.qty)) - inFlight;
      for (let i = 0; i < atRest; i++) out.push({ sub, helping: i });
    }
    return out;
  }, [contents, solidInFlight, pour?.substanceId]);
  // What the mixture is actually doing right now — the same facts the step
  // log's result line reports, shown live beside the vessel instead of only
  // on request. Empty while a fresh mix is still loading, so the card never
  // shows a stale reaction for contents that have already changed.
  const reactionHeadline = outcome && !loading ? headlineItems(outcome, celsius) : [];
  const reactionEquation = outcome && !loading ? outcome.equation : undefined;
  // How long the latest reaction takes, and how long the bench takes to show
  // it — see reactionPace. Only on the quantitative bench: the free bench has
  // no amounts, so nothing to use up and no run to time, and it keeps fizzing
  // for as long as the outcome says a gas is evolved, exactly as before.
  const realSeconds = useMemo(
    () => realReactionSeconds(working, outcome, contents, bandFor(celsius)),
    [working, outcome, contents, celsius],
  );
  const paceMs = playbackMs(realSeconds);
  const runPhase = useReactionRun(reactionRunKey(working), paceMs);
  const remaining = useMemo(() => remainingFractions(working), [working]);
  const hasGas = !!outcome?.gasEvolution && (!working || runPhase !== 'done');
  // A visibly colored gas (SO2, NO2) gets a denser, colored plume escaping
  // the rim on top of the ordinary in-liquid bubbles — colorless gases (H2,
  // CO2) stay bubbles-only, since they're not actually visible billowing out.
  const fumeColor = fumeColorFor(outcome?.gasEvolution);
  const hasPrecipitate = !!outcome?.precipitate && outcome.precipitate.amount !== 'none';
  // A vapor-phase outcome (see types.ts's `phase` doc) reuses `precipitate`
  // for the visible smoke/fumes two gases produce meeting in open air — a
  // settled layer on the vessel FLOOR is the wrong visual for that (there is
  // no liquid for anything to settle in); it reads as fumes near the RIM
  // instead, same as fumeColor's own puffs below.
  const isVaporFumes = hasPrecipitate && outcome?.phase === 'vapor';
  // Independent of any burner — an intrinsically exothermic reaction (e.g.
  // diluting an acid) releases heat with no flame involved.
  // On the quantitative bench the heat comes off while the reaction runs and
  // stops with it, like the gas: a reaction that has finished is not still
  // releasing anything.
  const isExothermic = outcome?.temperatureChange?.direction === 'exothermic' && (!working || runPhase !== 'done');
  /** A reaction hot enough to steam. `violent` reaches the boiling render on
   *  its own through the band; this is the step below it, which used to look
   *  identical to warming a flask by a degree. */
  const isSteaming = isExothermic
    && (outcome?.temperatureChange?.magnitude === 'strong'
      || outcome?.temperatureChange?.magnitude === 'violent');
  const rimY = rimYPx(vesselType);
  /** How much liquid the vessel is actually holding. Drops count, at 20 to the
   *  millilitre; solids do not — a spatula of salt sinks into the water rather
   *  than raising it, and a gas is not poured in at all. */
  const liquidML = useMemo(() => liquidVolumeML(contents), [contents]);
  const liquidTopY = liquidTopYPx(vesselType, liquidML);
  /** Where the liquid stands, as a fraction of the vessel's height — what the
   *  silhouette and the surface ellipse are both cut at. */
  const restingFraction = Math.min(1, (liquidTopY / rimY) || 0);
  /** Where the liquid stands THIS frame: its resting level, or a share of it
   *  while the vessel is filling. */
  const fillFraction = risen === null ? restingFraction : restingFraction * easeRise(risen);
  // How much of the vessel's neck sits empty above the liquid surface — a
  // flask's cone narrows into a neck well above its resting fill level, so
  // rim-hugging fume puffs alone left that whole stretch of glass looking
  // like the gas just wasn't there yet between the surface and the mouth.
  const neckGapPx = Math.max(0, liquidTopY - rimY);
  /* The stream's shape, wanted both here and in the vessel's <defs>. Narrow
     where it leaves the bottle, wider where it lands, sides bowed rather than
     ruled — a rectangle in one flat colour read as a pipe. */
  const pourFromY = rimY - POUR_ABOVE_RIM_PX;
  /* Drawn all the way to the floor and CUT at the surface, rather than stopped
     at it. Stopping it there is wrong the moment the level moves, and hiding
     the rest behind the liquid only worked while the liquid was opaque — a
     clear one showed the stream carrying on underwater like a pipe. */
  const pourToY = -2;
  const streamPath = (() => {
    const top = POUR_STREAM_W / 2;
    const bottom = top * 1.7;
    const mid = (pourFromY + pourToY) / 2;
    return `M ${-top} ${pourFromY} L ${top} ${pourFromY}`
      + ` Q ${top * 1.35} ${mid} ${bottom} ${pourToY}`
      + ` L ${-bottom} ${pourToY}`
      + ` Q ${-top * 1.35} ${mid} ${-top} ${pourFromY} Z`;
  })();

  const clipId = `liquid-clip-${containerId}`;
  const pourGradId = `pour-${containerId}`;
  const pourFlowId = `pour-flow-${containerId}`;
  const pourClipId = `pour-clip-${containerId}`;
  const pourAboveId = `pour-above-${containerId}`;
  const belowSurfaceId = `below-surface-${containerId}`;
  const glassId = `glass-${containerId}`;
  const sheenId = `sheen-${containerId}`;
  const liquidId = `liquid-${containerId}`;
  const surfaceId = `surface-${containerId}`;

  const stillBubbles = useMemo(() => Array.from({ length: STILL_BUBBLE_COUNT }, () => {
    // Biased low: that's where the glass is widest, so a bubble there is
    // clearly inside the liquid rather than clipped against a wall.
    const depth = 0.2 + Math.random() * 0.62;
    return {
      x: (Math.random() - 0.5) * art.floorHalfWidthPx * 1.35,
      y: liquidTopY * depth,
      r: 2.6 + Math.random() * 4.4,
      /* Its own faintness. One opacity for all of them made a row of identical
         pale discs — a pattern, and a bright one; bubbles at different depths
         in a coloured liquid are seen through different amounts of it, and
         some are barely there at all. Well under half, because these are a
         suggestion of bubbles caught in the glass, not the subject. */
      alpha: 0.14 + Math.random() * 0.3,
    };
  }), [art.floorHalfWidthPx, liquidTopY]);

  // A precipitating mixture isn't a static fill — it's solid actively
  // forming and sinking out of solution. Reuses the gas-bubble pattern
  // (small shapes, staggered delay/duration, looping) but travelling the
  // other direction — drifting down from the surface instead of rising from
  // the floor — and fading out into the settled layer once they arrive
  // rather than popping at the surface, so the settled layer below reads as
  // where they've been accumulating rather than a separate, unrelated fact.
  const precAmount = outcome?.precipitate?.amount;
  const precipitateParticleCount = precAmount === 'heavy' ? 32 : precAmount === 'light' ? 18 : 0;
  const precColor = outcome?.precipitate ? resolveColorHex(outcome.precipitate.colorHex, outcome.precipitate.color) : '#ffffff';
  // A reaction precipitates fastest right when the reactants meet and slows
  // as they're used up — modelled as a plain geometric decay (5% off the
  // PREVIOUS second's rate, not a fixed 5%-of-original-per-second, so it
  // eases off rather than stopping abruptly). How many particles are still
  // actively falling at time t is rate(t) = rate(0) * 0.95^t; how much has
  // settled by time t is the same decay's complement, 1 - 0.95^t — the two
  // move together by construction; falling slows exactly as settled grows.
  // Restarts whenever a new outcome arrives, since that's a fresh reaction.
  const [precipitateElapsedS, setPrecipitateElapsedS] = useState(0);
  useEffect(() => {
    if (!hasPrecipitate) return;
    setPrecipitateElapsedS(0);
    const id = window.setInterval(() => setPrecipitateElapsedS(s => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [outcome, hasPrecipitate]);
  const precipitateDecay = Math.pow(0.95, precipitateElapsedS);
  const precipitateSettledFraction = 1 - precipitateDecay;
  // See liquidColor()'s own doc: fades from the pre-reaction colour toward
  // the model's stated residual colour as precipitateSettledFraction rises,
  // rather than snapping straight to one or the other.
  const color = liquidColor(substances, outcome, precipitateSettledFraction);
  // A white precipitate settling in a liquid the SAME white is invisible —
  // darkening the liquid a notch keeps the precipitate reading as a distinct
  // white solid rather than the two blending into one flat mass. Derived
  // FROM the liquid's own resolved colour (not a fixed hardcoded hue like
  // the cream this replaced) — a colorless solution stays looking colorless
  // instead of reading as a wrong, tinted liquid.
  const precIsWhite = hasPrecipitate && isNearWhite(precColor);
  const liquidFillColor = precIsWhite ? darken(color, 0.12) : color;

  /**
   * A liquid with no colour of its own is drawn TRANSPARENT rather than in
   * some pale colour that stands for clear.
   *
   * That is the whole of what "colourless" means: you see the bench through
   * the glass. Alpha gets it right on both themes for free — dark bench, dark
   * liquid; pale bench, pale liquid — where a stored tint had to be a
   * compromise between the two and came out as milk on the dark one. Nothing
   * about the substance is theme-aware, which is right: copper sulfate is blue
   * on anybody's bench.
   */
  const isClear = liquidFillColor === COLOURLESS;
  /**
   * How much of the bench a clear liquid lets through: enough that the glass
   * and the ground behind it read, not so much that the level stops being
   * obvious.
   *
   * Concentrated acid keeps more of the light than a solution does. Both are
   * colourless, so neither is TINTED — what separates them is that one is
   * dense and oily and the other is water, and body is the thing alpha is
   * actually for.
   */
  const clearAlpha = substances.some(sub => !isWatery(sub)) ? 0.58 : 0.38;

  // The glass-shine stripe and the still air bubbles are there to sell an
  // otherwise-empty vessel as transparent glass — once the liquid itself has
  // a colour to look at (a reaction's colorChange, not just its neutral
  // starting tint), they stop helping and start reading as white clutter
  // sitting on top of the colour.
  const isColoredSolution = !!outcome?.colorChange;
  // Fine flecks/fibres, not uniform balls — closer to how a real flocculent
  // precipitate (AgCl's curdy clumps, Fe(OH)2's fluffy flakes) actually
  // looks than a perfect circle does, which reads as a bubble.
  const precipitateParticles = useMemo(() => Array.from({ length: precipitateParticleCount }, (_, i) => ({
    x: (Math.random() - 0.5) * art.floorHalfWidthPx * 1.6,
    len: 1 + Math.random() * 1.5,
    thick: 0.33 + Math.random() * 0.3,
    angle: Math.random() * 180,
    delay: (i / Math.max(1, precipitateParticleCount)) * 3.2,
    duration: 2.6 + Math.random() * 1.8,
  })), [precipitateParticleCount, art.floorHalfWidthPx]);

  /**
   * How hard the gas comes off, which is a fact the outcome now carries
   * (gasEvolution.rate) and used not to: every reaction fizzed identically, so
   * a narration saying "vigorously" sat over seven lazy bubbles. Three things
   * move together, because that is what separates a trickle from a froth —
   * how MANY there are, how FAST they go, and how BIG they are.
   *
   * `steady` is what everything drew before, so an outcome from before the
   * field existed looks exactly as it did.
   */
  const fizz = FIZZ[outcome?.gasEvolution?.rate ?? 'steady'];
  const bubbles = useMemo(() => Array.from({ length: fizz.count }, (_, i) => ({
    x: (Math.random() - 0.5) * art.rimHalfWidthPx * 1.1,
    delay: (i / fizz.count) * fizz.spread,
    duration: fizz.duration + Math.random() * 0.6,
  })), [art.rimHalfWidthPx, fizz]);

  // Nucleate on the FLOOR and grow on the way up — that, plus the steam and
  // the churning surface below, is what separates boiling from a reaction
  // fizzing gas off.
  const boilBubbles = useMemo(() => Array.from({ length: BOIL_BUBBLE_COUNT }, (_, i) => ({
    x: (Math.random() - 0.5) * art.floorHalfWidthPx * 1.5,
    r: 3.4 + Math.random() * 3.6,
    delay: (i / BOIL_BUBBLE_COUNT) * 1,
    duration: 0.75 + Math.random() * 0.5,
  })), [art.floorHalfWidthPx]);

  const steamPuffs = useMemo(() => Array.from({ length: STEAM_COUNT }, (_, i) => ({
    x: (Math.random() - 0.5) * art.rimHalfWidthPx * 0.8,
    r: 6 + Math.random() * 5,
    dx: (Math.random() - 0.5) * 30,
    dy: -60 - Math.random() * 40,
    delay: (i / STEAM_COUNT) * 2.2,
    duration: 1.9 + Math.random() * 0.8,
  })), [art.rimHalfWidthPx]);

  const smokePuffs = useMemo(() => Array.from({ length: SMOKE_COUNT }, (_, i) => ({
    x: (Math.random() - 0.5) * art.rimHalfWidthPx * 0.5,
    r: 5 + Math.random() * 4,
    dx: (Math.random() - 0.5) * 46,
    dy: -46 - Math.random() * 30,
    delay: (i / SMOKE_COUNT) * 2.2,
    duration: 2 + Math.random() * 0.8,
  })), [art.rimHalfWidthPx]);

  // Denser, faster than smoke — reads as "billowing straight out" rather
  // than a slow wispy haze. Spread was originally tighter (0.3x) than
  // smoke's, but at the neck's small on-screen scale that clustered all
  // FUMES_COUNT puffs into what read as one small static dot instead of a
  // visible plume of a distinctly-colored gas (e.g. NO2) — widened to
  // smoke's own 0.5x so the puffs actually fan out enough to register as
  // gas escaping, not just a blob sitting on the rim.
  // dy's spread matters as much as its average: a narrow range (e.g. all 8
  // puffs topping out within a 20px band) makes every puff cap its rise at
  // nearly the same height, and since several are always mid-flight at any
  // instant, the ensemble reads as a cloud with a flat, cut-off ceiling
  // instead of a natural taper — not an actual clip (verified: nothing in
  // the DOM/CSS bounds the puffs there), just an artifact of them all
  // agreeing on how high "up" is. Widened to a 50px spread so some puffs
  // fade out low and others sail higher, breaking that shared ceiling.
  const fumesPuffs = useMemo(() => Array.from({ length: FUMES_COUNT }, (_, i) => ({
    x: (Math.random() - 0.5) * art.rimHalfWidthPx * 0.5,
    r: 7 + Math.random() * 5,
    dx: (Math.random() - 0.5) * 30,
    dy: -30 - Math.random() * 50,
    delay: (i / FUMES_COUNT) * 1.1,
    duration: 0.9 + Math.random() * 0.4,
  })), [art.rimHalfWidthPx]);

  /*

   * The overlay pass. Same transforms as the body, so a card lands exactly

   * where it always did — only later in the document, which in SVG is the

   * only thing that means "on top".

   *

   * Not clickable: the vessel underneath owns selection, and a transparent

   * layer over the whole bench would swallow every click meant for glassware.

   */

  if (layer === 'overlay') {

    /*
     * A block may overlap the glassware — it may not leave the picture.
     *
     * Its natural place is a fixed distance from the rim, which is a distance
     * in SCENE units: on a narrow panel the same flask is drawn much larger
     * relative to the frame, so "beside the vessel" walked the contents block
     * off the left edge and cut it in half. Clamped to the scene's own edges
     * here — overlapping the vessel is what the blocks are folded and dragged
     * for, and is in every case better than a label you cannot finish reading.
     *
     * In this group's coordinates the scene's left edge is at
     * -(columnXPx + offsetXPx), since that is where the vessel was translated
     * to; the right edge is that plus the scene's width.
     */
    const sceneLeft = -(columnXPx + offsetXPx);
    const sceneRight = sceneLeft + sceneWidthPx;
    const insideScene = (x: number, boxWidth: number): number =>
      // Left last, so on a panel too narrow for the block at all it is the
      // left edge that wins and the block is cut off screen-RIGHT, where the
      // reader is already used to a line continuing.
      Math.max(sceneLeft + BLOCK_EDGE_PAD, Math.min(x, sceneRight - boxWidth - BLOCK_EDGE_PAD));

    /**
     * The reaction card, sized to the room to the vessel's RIGHT.
     *
     * It was a fixed width, and `insideScene` keeps a box on screen by sliding
     * it back from the edge — so on a bench narrower than the card assumed (the
     * quantitative bench gives its notebook twice the width) the card slid left
     * over the glass and over the contents block beside it. Narrower, with its
     * text wrapping, is better than on top of the thing it describes; it gets
     * taller in proportion so a wrapped equation is not cut off.
     */
    const roomRight = sceneRight - BLOCK_EDGE_PAD - (art.rimHalfWidthPx + labelGap + (blocks.reaction.dx ?? 0));
    const reactionFitW = Math.max(REACTION_CARD_MIN_WIDTH * textScale, Math.min(reactionW, roomRight));
    // Room for the numbers too, when the card carries them: a conclusion and a
    // line per product it names. The box is only the drawable area — the card
    // inside sizes to its content — so a generous allowance costs nothing.
    const amountsH = working ? (isSpedUp(realSeconds) ? 150 : 110) * textScale : 0;
    const reactionFitH = reactionH * Math.min(2, reactionW / reactionFitW) + amountsH;
    /*
     * No room to the right — a phone, where the flask is most of the width —
     * and the card was pushed back left by insideScene, straight over the
     * contents block on the other side of the same vessel. There the card
     * goes ABOVE the mouth instead, centred on the vessel: still against the
     * glass it describes, in the headroom a bench always has over its
     * glassware, and clear of the contents block beside the rim. Its box is
     * anchored by its bottom edge, so the card sits just over the mouth
     * however tall it turns out. A drag still moves it from there, and keeps
     * the offset relative to the vessel as before.
     */
    const reactionAbove = roomRight < REACTION_CARD_MIN_WIDTH * textScale;
    const reactionAboveW = Math.min(reactionW, sceneWidthPx - 2 * BLOCK_EDGE_PAD);

    return (

      <g transform={`translate(${offsetXPx}, ${-risePx})`} pointerEvents="none">
        {/* Nothing to say about an empty vessel, so it says nothing: a bench of
            fresh glassware drew a row of "Empty" chips, which is noise where
            the glass already shows it. The selected one keeps its block —
            that is the vessel about to be poured into, and the block is where
            what lands in it will appear. */}
        {(substances.length > 0 || isSelected) && (() => {
          const w = blocks.info.minimised ? foldedPx : labelW;
          return (
            // Beside the vessel, not over it: a long mixture name stacked above
            // the mouth grew a chip taller than the glassware it named, and on a
            // rod stand it collided with the clamp. Anchored to the left because
            // that is the side a rod never occupies. Offset from rimHalfWidthPx
            // (not maxHalfWidthPx) since the box sits right at the rim — a
            // flask/round-flask's neck is much narrower than its base, and
            // anchoring off the base left a wide, empty-looking gap to the neck.
            <foreignObject
              x={insideScene(-(art.rimHalfWidthPx + labelGap + w) + (blocks.info.dx ?? 0), w)}
              y={rimY - 4 + (blocks.info.dy ?? 0)}
              width={w} height={labelH}
            >
              {/* Against the glass, not the slot's far edge. The slot is sized
                  for the longest caption, and a short one ("Empty", "Water")
                  sat at its outer end — on a phone, clamped to the scene's
                  wall a hand's width from the flask it named. */}
              <div className="chemlab-block-slot-end">
                <BenchBlock
                  kind="info" title="Contents" icon={<ContentsIcon />}
                  state={blocks.info} zoom={zoom}
                  onChange={(next) => onBlockChange('info', next)}
                  style={{ maxWidth: labelW }}
                >
                  <div
                      ref={labelRef}
                      className="chemlab-beaker-label chemlab-beaker-label-light"
                      /* An ABSOLUTE cap, not a percentage. Inside a foreignObject the
                         containing block is effectively indefinite, so `max-width:100%`
                         and `width: fit-content` both resolve against nothing — a long
                         mixture name drew a 217px chip in a 126px slot and spilled
                         across the glassware beside it. The slot's own width is the
                         only number that means anything here. The width beside it is
                         the measured one; until this caption has been measured it is
                         absent, which is the slot's width and how it gets measured. */
                      style={{ maxWidth: labelW, width: labelWidth }}
                    >
                    {/* The text alone, so the measurement is of the words and not
                        of the box they are in. */}
                    <span ref={labelTextRef}>
                      {substances.length === 0
                        ? <span className="chemlab-beaker-empty">Empty</span>
                        /* With quantities on, each reagent says how much of it
                           is in there, in its own unit. A gas has no dose (it
                           arrived through a tube), so it is named and no more —
                           "(0 mL)" beside it would be a claim about an amount
                           nobody measured. */
                        : contents.map(({ substanceId, qty, concentrationM }) => {
                          const substance = findSubstance(substanceId);
                          if (!substance) return null;
                          const amount = showQuantities ? formatAmount(substance, qty) : '';
                          if (!amount) return substance.displayName;
                          // On the quantitative bench a count or a volume is
                          // only half an amount: counted pieces name their mass,
                          // and a solution its strength — the one the student
                          // chose, or the bottle's own.
                          const mass = countedMass(substance, qty);
                          if (mass) return `${substance.displayName} (${amount}, ${mass})`;
                          const strength = concentrationM ?? substance.concentrationM;
                          return strength !== undefined && substance.molarities
                            ? `${substance.displayName} (${amount}, ${formatMolarity(strength)})`
                            : `${substance.displayName} (${amount})`;
                        }).filter(Boolean).join(' + ')}
                      {loading && (
                        <span className="chemlab-beaker-loading"> · {substances.length <= 1 ? 'checking…' : 'reacting…'}</span>
                      )}
                    </span>
                    {/* The menu, findable. Holding the glassware opens the same
                        one, but nothing on a bench says that holding is a thing
                        you can do — and this block is already the vessel's own
                        caption. In the block's own gutter under the contents
                        mark, not at the end of the words: on the end of a line
                        of text it read as an ellipsis. */}
                    <button
                      type="button"
                      className="chemlab-block-menu"
                      title="What you can do with this container"
                      aria-label="Container options"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onRequestMenu(e.clientX, e.clientY); }}
                    >
                      <MenuIcon />
                    </button>
                  </div>
                </BenchBlock>
              </div>
            </foreignObject>
          );
        })()}

        {(reactionHeadline.length > 0 || reactionEquation) && (
          // Mirrors the contents label but on the vessel's other side — the
          // side a rod clamp never occupies either — so what the mixture is
          // DOING sits right next to what it's made of, both readable at a
          // glance without opening a step's "More" detail.
          (() => {
          const cardW = reactionAbove ? reactionAboveW : reactionFitW;
          const boxW = blocks.reaction.minimised ? foldedPx : cardW;
          const boxH = blocks.reaction.minimised ? foldedPx : reactionH + amountsH;
          return (
          <foreignObject
            /* Above the mouth, the slot is the bench's whole width and the card
               is placed inside it — centred on the vessel, then moved by the
               drag. Clamping a box as wide as the bench kept it from moving
               sideways at all. */
            x={reactionAbove
              ? sceneLeft + BLOCK_EDGE_PAD
              : insideScene(art.rimHalfWidthPx + labelGap + (blocks.reaction.dx ?? 0), boxW)}
            y={(reactionAbove ? rimY - 4 - labelGap - boxH : rimY - 4) + (blocks.reaction.dy ?? 0)}
            width={reactionAbove ? sceneWidthPx - 2 * BLOCK_EDGE_PAD : boxW}
            height={reactionAbove ? boxH : (blocks.reaction.minimised ? foldedPx : reactionFitH)}
          >
            <div
              className={reactionAbove ? 'chemlab-block-slot-above' : undefined}
              style={reactionAbove
                ? { ['--chemlab-card-x' as string]: `${-(sceneLeft + BLOCK_EDGE_PAD) + (blocks.reaction.dx ?? 0)}px` }
                : undefined}
            >
            <BenchBlock
              kind="reaction" title="Reaction" icon={<ReactionIcon />}
              state={blocks.reaction} zoom={zoom}
              onChange={(next) => onBlockChange('reaction', next)}
              style={{ maxWidth: cardW }}
            >
              <div className="chemlab-reaction-card" style={{ maxWidth: cardW }}>
                {reactionEquation && (
                  <div className="chemlab-reaction-card-equation">
                    <ChemEquation equation={reactionEquation} />
                  </div>
                )}
                {reactionHeadline.map((item, i) => (
                  <div key={i} className="chemlab-reaction-card-item">
                    {item.swatch
                      ? <span className="chemlab-reaction-card-swatch" style={{ background: item.swatch }} />
                      : <span aria-hidden="true">{item.icon}</span>}
                    <span>{item.text}</span>
                  </div>
                ))}
                {/* On the quantitative bench the card says what happened in
                    NUMBERS too: which reagent ran out, what is left, and what
                    was made — the gas as a volume, a solid as a mass. The same
                    figures as the notebook's table, beside the glass they
                    describe. */}
                {working && (
                  <div className="chemlab-reaction-card-amounts">
                    <p>{conclusionOf(working)}</p>
                    {working.products
                      .filter(p => p.volumeL !== null || (p.massG !== null && p.concentrationM === null && p.formula !== 'H2O'))
                      .map(p => (
                        <p key={p.formula}>
                          {formatChemistry(p.formula)} made: {formatMoles(p.mol)}
                          {p.volumeL !== null
                            ? ` = ${formatVolume(p.volumeL)} at RTP`
                            : p.massG !== null ? ` = ${formatMass(p.massG)}` : ''}
                        </p>
                      ))}
                    {/* Said, because the bench cannot show it at its real
                        pace: a student who watched marble chips vanish in ten
                        seconds should not leave thinking that is how long it
                        takes. */}
                    {isSpedUp(realSeconds) && (
                      <p className="chemlab-reaction-card-pace">
                        Sped up: on a real bench this takes {formatRealDuration(realSeconds)}; shown here in {PLAYBACK_CAP_SECONDS} seconds.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </BenchBlock>
            </div>
          </foreignObject>
          );
          })()
        )}

      </g>

    );

  }


  return (
    <g
      onClick={(e) => {
        e.stopPropagation();
        // A finger's second way to the menu, needing no hold: tap a vessel to
        // pick it, tap it again for what you can do with it. A mouse keeps its
        // right-click, and tapping a picked vessel did nothing before.
        if (isSelected && coarsePointer()) onRequestMenu(e.clientX, e.clientY);
        else onClick();
      }}
      {...longPress}
      style={{ cursor: 'pointer' }}
    >
      {/* The swirl rocks the WHOLE vessel — glass, liquid, surface and
          everything settled in it — because that is what a hand does to a
          flask: it does not stir the contents, it moves the vessel and lets
          the contents follow. Keyed by the token so swirling twice running
          restarts the motion rather than being ignored as the same class. */}
      <g key={swirlToken ?? 'still'} className={swirlToken ? 'chemlab-swirl' : undefined}>
      <g transform={`translate(${offsetXPx}, ${-risePx})`}>
        <defs>
          <clipPath id={clipId}>
            <path d={art.outlinePath} />
          </clipPath>
          {/* Everything below the liquid's surface, wherever it stands this
              frame — what the wall in front of the liquid is clipped to. */}
          <clipPath id={belowSurfaceId}>
            <rect
              x={-art.maxHalfWidthPx * 2} y={rimY * fillFraction}
              width={art.maxHalfWidthPx * 4} height={Math.max(1, -rimY * fillFraction + 4)}
            />
          </clipPath>
          <linearGradient id={glassId} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={GLASS_BOTTOM} stopOpacity={GLASS_OPACITY} />
            <stop offset="100%" stopColor={GLASS_TOP} stopOpacity={GLASS_OPACITY} />
          </linearGradient>
          {/* Left-lit: a bright edge, a fast falloff, then a faint rim-light
              on the right. One gradient does for every shape — no per-vessel
              highlight geometry to keep in sync with the profiles. */}
          <linearGradient id={sheenId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#fff" stopOpacity={0.5} />
            <stop offset="32%" stopColor="#fff" stopOpacity={0.03} />
            <stop offset="88%" stopColor="#fff" stopOpacity={0.02} />
            <stop offset="100%" stopColor="#fff" stopOpacity={0.2} />
          </linearGradient>
          {pouring && (
            <>
              {/* Lit like the glassware: pale down the left where the light
                  is, its own colour through the middle, deeper at the right
                  edge — which is what turns a strip into a round column. */}
              <linearGradient id={pourGradId} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={lighten(pouring.color, 0.5)} />
                <stop offset="38%" stopColor={pouring.color} />
                <stop offset="100%" stopColor={darken(pouring.color, 0.2)} />
              </linearGradient>
              {/* The travelling highlight — a soft band, not an edge. */}
              <linearGradient id={pourFlowId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fff" stopOpacity={0} />
                <stop offset="50%" stopColor="#fff" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#fff" stopOpacity={0} />
              </linearGradient>
              <clipPath id={pourClipId}><path d={streamPath} /></clipPath>
              {/* Everything above the liquid's surface, wherever that has got
                  to this frame. A stream is only visible until it goes in. */}
              <clipPath id={pourAboveId}>
                <rect
                  x={-art.maxHalfWidthPx * 2} y={pourFromY - 40}
                  width={art.maxHalfWidthPx * 4}
                  height={Math.max(1, (hasLiquid ? rimY * fillFraction : -2) - (pourFromY - 40))}
                />
              </clipPath>
            </>
          )}
          {/*
            * The liquid's SURFACE, which is a plane seen from a little above
            * and not a lid laid on top.
            *
            * A flat lighter fill is what made it read as a disc: one tone, a
            * hard edge all round, and noticeably paler than the body under it.
            * What you actually see across a surface is a gradient — the far
            * edge sits in the shadow of the wall behind it and is DARKER than
            * the liquid, the near edge catches the light and is lighter, and
            * the middle is simply the liquid. Same reasoning as the mouth's
            * bore above, and the same fix.
            */}
          <linearGradient id={surfaceId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={darken(liquidFillColor, 0.12)} />
            <stop offset="45%" stopColor={liquidFillColor} />
            <stop offset="100%" stopColor={lighten(liquidFillColor, 0.3)} />
          </linearGradient>
          <linearGradient id={liquidId} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={darken(liquidFillColor, 0.16)} />
            <stop offset="55%" stopColor={liquidFillColor} />
            <stop offset="100%" stopColor={lighten(liquidFillColor, 0.18)} />
          </linearGradient>
        </defs>

        {/* The glass itself — a filled body, not an empty outline. Also the
            hit target: without a fill, a click anywhere inside an empty
            vessel's silhouette would hit nothing at all. */}
        <path d={art.outlinePath} fill={`url(#${glassId})`} />

        {/* Down through the mouth. Without something here the opening is the
            same tone as the body and the rim band reads as a disc rather than
            a hole. What that something is belongs to the theme
            (--lab-glass-bore-fill, --lab-glass-bore): on a dark bench a flat
            black wash reads as depth, and on a pale one the same wash is a
            grey lid — there it is the gradient above, which lifts the lit far
            wall and shades only the deep near edge. */}
        <path d={art.mouthInnerPath} className="chemlab-glass-bore" />

        {added && pour && stages.total > 0 && (() => {
          // Where the stream ends, and where the ring is drawn, are two
          // different heights while a level is rising. The stream runs all the
          // way to the FLOOR: it is drawn under the liquid (see the order
          // below), so the rising level swallows it from the bottom up, which
          // is what a stream going into a filling vessel looks like. A solid
          // lands on whatever is already there.
          const fromY = pourFromY;

          return (
            <g key={pour.token} pointerEvents="none">
              {/* What a solid is tipped off. Measured in grams means a
                  spatula; measured in pieces means a hand, which is drawn as
                  nothing at all rather than as the wrong tool. */}
              {added?.dose?.unit === 'g' && stages.total > 0 && (
                <g transform={`translate(0, ${fromY})`}>
                  <g
                    className="chemlab-spatula"
                    style={{
                      animationDuration: `${stages.total}ms`,
                      // The pivot is the bowl's lip, which is the drawing's
                      // own origin — stated from the geometry because a
                      // fill-box origin is measured from the box's corner.
                      transformOrigin: `${SPATULA_BOWL_RX * 1.2}px ${SPATULA_BOWL_RY + 3.4}px`,
                      ['--chemlab-spatula-tip' as string]: `${SPATULA_TIP_DEG}deg`,
                      ['--chemlab-spatula-rest' as string]: `${SPATULA_REST_DEG}deg`,
                    }}
                  >
                    <Spatula substance={added} />
                  </g>
                </g>
              )}
              {dropping ? (
                <DropperDrops
                  substance={dropping}
                  // How many drops this add put in, which is the dose itself:
                  // a repeat add is its own pour with its own two drops, not a
                  // re-run of everything the vessel holds.
                  count={perAddFor(dropping, vesselType)}
                  fromY={fromY}
                  toY={hasLiquid ? rimY * fillFraction : pourToY}
                  clear={dropping.color === COLOURLESS}
                />
              ) : pouring ? (
                <>
                {/* Where the liquid is coming from. Placed at the head of the
                    stream and rotated about that point, so the neck stays put
                    while the bottle tips over it and back. */}
                <g transform={`translate(0, ${fromY})`}>
                  <g
                    className="chemlab-pour-bottle"
                    style={{
                      animationDuration: `${stages.total}ms`,
                      // The pivot is the bottle's NECK, which is its local
                      // origin — and a fill-box origin is measured from the
                      // bounding box's corner, so it is stated here from the
                      // geometry rather than guessed as a percentage.
                      transformOrigin: `${BOTTLE_W}px ${BOTTLE_H}px`,
                      ['--chemlab-bottle-tip' as string]: `${POUR_BOTTLE_TIP_DEG}deg`,
                      ['--chemlab-bottle-rest' as string]: `${POUR_BOTTLE_REST_DEG}deg`,
                    }}
                  >
                    <PourBottle substance={pouring} clear={pouring.color === COLOURLESS} />
                  </g>
                </g>
                /*
                 * A pour has three stages, and they are three different
                 * movements — one element doing all of it is what made the
                 * middle one wrong.
                 *
                 * 1. It falls. The ribbon grows downward from the bottle to
                 *    the bottom of the vessel: fixed time, accelerating.
                 * 2. It pours. The ribbon stands at full length and the LEVEL
                 *    answers. The ribbon is CUT at the surface (pourAboveId),
                 *    so what you see is a stream ending on a surface that is
                 *    climbing towards the bottle. It used to be drawn under
                 *    the liquid and hidden by it, which stopped working the
                 *    moment a clear liquid became see-through: the stream
                 *    carried on underwater like a pipe.
                 * 3. It stops. The top of the ribbon detaches and the last of
                 *    it falls in — the stream shortens from the TOP, its
                 *    bottom staying on the surface. Fading it out instead read
                 *    as the stream evaporating.
                 *
                 * Two nested elements because stages 1 and 3 scale the same
                 * ribbon about opposite ends, and one element can only hold
                 * one transform: the group scales about the bottle, the rect
                 * inside it about the landing point.
                 */
                <g clipPath={`url(#${pourAboveId})`}>
                    <g className="chemlab-pour-fall" style={{ animationDuration: `${stages.fall}ms` }}>
                    <g
                      className="chemlab-pour-tail"
                      style={{
                        animationDuration: `${stages.tail}ms`,
                        animationDelay: `${stages.fall + stages.rise}ms`,
                      }}
                    >
                      {/* The ribbon itself. A rectangle in one flat colour read
                          as a pipe running out of the bottle, so it is a shape
                          and a surface instead:

                          - it WIDENS as it comes down and its sides bow out,
                            the way a poured stream spreads and wobbles rather
                            than holding a machined edge;
                          - it is lit like the glassware — pale down the left
                            where the light is, its own colour through the
                            middle, deeper at the right edge — so it reads as a
                            round column of liquid rather than a painted strip;
                          - and it MOVES: a highlight runs down it on a short
                            loop, which is the only thing in the picture that
                            says the liquid inside it is travelling. Without it
                            a stream at full length is a static object, and the
                            eye stops believing it after about a second. */}
                      <path
                        d={streamPath} fill={`url(#${pourGradId})`}
                        fillOpacity={pouring.color !== COLOURLESS ? 1 : isWatery(pouring) ? 0.5 : 0.7}
                      />
                      <g clipPath={`url(#${pourClipId})`}>
                        <rect
                          className="chemlab-pour-flow"
                          x={-POUR_STREAM_W} y={pourFromY}
                          width={POUR_STREAM_W * 2} height={POUR_FLOW_H}
                          fill={`url(#${pourFlowId})`}
                          style={{ ['--chemlab-flow-to' as string]: `${Math.max(1, pourToY - pourFromY)}px` }}
                        />
                      </g>
                      </g>
                    </g>
                </g>
                </>
              ) : (
                (() => {
                  /*
                   * A dry vessel takes a HEAP: tipped off a spatula onto glass,
                   * the pieces stay where they land and pile up. Into liquid
                   * they do not — they hit the surface at different moments,
                   * drift as they sink and come to rest across the floor. So
                   * the same pieces are dealt wider and lie flatter when there
                   * is something for them to fall through.
                   */
                  /* The helping this one will be once it has landed: the last
                     one the quantity accounts for, so the falling pieces and
                     the resting ones share a seed and land where they were
                     drawn falling to. */
                  const helping = Math.max(0, helpingsOf(
                    added, contents.find(c => c.substanceId === added.id)?.qty ?? 0,
                  ) - 1);
                  const { motion, pieces } = solidPieces(
                    seedOf(`${containerId}:${added.id}:${helping}:${swirls}`), added.form,
                    hasLiquid ? 1.5 : 1, art.floorHalfWidthPx,
                  );
                  return pieces.map((piece, i) => (
                    // The fall is on the group and the tilt on the shape
                    // inside it. Both on one element and the animated
                    // transform simply replaces the static one — the piece
                    // would fall flat, with no tilt at all. (The precipitate's
                    // flecks are built the same way, for the same reason.)
                    // A fluttering piece wants a THIRD element, since its
                    // rocking is a transform too.
                    <g
                      key={i}
                      className={`chemlab-solid-drop chemlab-solid-${motion}`}
                      style={{
                        /* To the FLOOR, which is where it will lie — not to
                           the surface of whatever is already in there. A piece
                           that stopped at the surface hung above the place it
                           then appeared at, and anything dropped into water
                           goes to the bottom anyway. */
                        ['--chemlab-fall' as string]: `${Math.max(1, piece.cy - fromY)}px`,
                        /* How far out this piece ends up from where it came in.
                           It holds this offset all the way down and gives it
                           back on the bounce — see the keyframes. */
                        ['--chemlab-drift' as string]: `${piece.x}px`,
                        animationDelay: `${piece.delay}s`,
                      }}
                    >
                      <g className={motion === 'flutter' ? 'chemlab-solid-rock' : undefined}>
                        <rect
                          x={piece.x - piece.w / 2} y={piece.cy - piece.h / 2}
                          width={piece.w} height={piece.h} rx={piece.rx}
                          transform={`rotate(${piece.spin} ${piece.x} ${piece.cy})`}
                          fill={added.color} stroke={darken(added.color, 0.25)} strokeWidth={0.6}
                        />
                      </g>
                    </g>
                  ));
                })()
              )}
            </g>
          );
        })()}

        {hasLiquid && (
          <g clipPath={`url(#${clipId})`}>
            {/* The liquid LAGS the glass through a swirl — it is what is being
                mixed, and it arrives at each side of the circle a moment after
                the vessel does. Inside the clip and not around it, so what
                rides up the wall is still cut off at the glass. */}
            <g className={swirlToken ? 'chemlab-swirl-liquid' : undefined}>
            <path
              d={art.liquidPath(fillFraction)} fill={`url(#${liquidId})`}
              fillOpacity={isClear ? clearAlpha : 1}
            />
            {/* The surface, lifted — the single strongest cue that the fill is
                a liquid with a top rather than a painted region. An ellipse,
                because the mouth above it is one: you are looking down onto
                the liquid, so you see its whole face rather than its edge.
                It moves whenever gas is breaking through it: hard when the
                mixture is boiling, barely when it is only fizzing — bubbles
                reaching a perfectly still surface is the tell that stops the
                effervescence reading as real. */}
            {/* Boiling and fizzing already move this; a pour landing on it is
                a third thing, and the quietest of the three — a stream into a
                beaker rocks the surface, it does not churn it. Boiling wins
                where they meet: it is the more violent fact about the same
                liquid. */}
            <g className={isBoiling ? 'chemlab-surface-churn'
              : landing ? 'chemlab-surface-pour'
                : hasGas && runPhase !== 'fading' ? 'chemlab-surface-fizz' : undefined}>
              {/* The surface stays stronger than the body it tops: the
                  meniscus of a clear liquid is the one part of it you CAN
                  see, and it is what says where the level is. */}
              <path
                d={art.surfacePath(fillFraction)}
                /* A coloured liquid's surface is its own colour lifted — the
                   face of it catching the light. A CLEAR one has no colour to
                   lift: lifting it anyway painted a white disc across the top
                   of the acid, which is the one thing a clear liquid never
                   has. Barely lifted and mostly transparent instead, so what
                   reads is a plane rather than a lid. */
                /* Shaded across rather than filled flat — see the gradient. A
                   clear liquid's surface is barely there either way, so it
                   keeps the plain treatment and most of its transparency. */
                fill={isClear ? lighten(liquidFillColor, 0.1) : `url(#${surfaceId})`}
                fillOpacity={isClear ? 0.26 : 1}
              />
              <path
                d={art.surfaceFrontArc(fillFraction)} fill="none"
                /* The waterline. On a coloured liquid it is a lifted band —
                   the top of the colour catching the light. On a clear one it
                   is a MENISCUS: a thin bright edge where the surface meets
                   the glass, and nothing else. At the coloured liquid's weight
                   it drew a white hoop across the acid and the whole surface
                   read as a disc laid on top of it. */
                /* Lifted from the liquid's OWN colour, not towards white. A
                   55% lift of a saturated teal is white for all practical
                   purposes, and a white line across a coloured liquid reads as
                   a painted edge rather than as light on water. A third of the
                   way is enough to separate the surface from the body. */
                stroke={lighten(liquidFillColor, isClear ? 0.55 : 0.32)}
                strokeWidth={isClear ? 1.1 : 2}
                strokeOpacity={isClear ? 0.55 : 0.85}
              />
            </g>
            {/* The few bubbles clinging inside any uncoloured liquid. Filled,
                they were white beads floating in the acid; in a clear liquid a
                bubble is a surface you see the edge of, so it is a ring. */}
            {!isColoredSolution && stillBubbles.map((b, i) => (
              isClear
                ? (
                  <circle
                    key={i} cx={b.x} cy={b.y} r={b.r}
                    fill="none" stroke={lighten(liquidFillColor, 0.5)} strokeWidth={0.8}
                    opacity={b.alpha + 0.18}
                  />
                )
                : (
                  <circle
                    key={i} cx={b.x} cy={b.y} r={b.r}
                    fill={lighten(liquidFillColor, 0.62)} opacity={b.alpha}
                  />
                )
            ))}
            {precipitateParticles.length > 0 && (() => {
              // Lighten/darken alone can't be trusted for contrast — a
              // precipitate's own colour (often white, e.g. AgCl) can be
              // nearly the same shade as the liquid it's forming in. A fixed
              // dark stroke on every particle keeps it legible regardless.
              const outline = darken(precColor, 0.55);
              // The sink animation drives one CSS transform (translate) on
              // the outer <g> — a static rotate on the inner shape is what
              // gives each fleck its own tilt, since a second, unanimated
              // transform on the SAME element would just be replaced by the
              // animated one rather than composing with it.
              // Only the still-falling share of the (fixed, pre-randomized)
              // particle pool is drawn — shrinking with precipitateDecay —
              // so fewer new ones appear as the reaction's rate falls off.
              const visibleCount = Math.round(precipitateParticleCount * precipitateDecay);
              return precipitateParticles.slice(0, visibleCount).map((p, i) => (
                <g
                  key={i}
                  className="chemlab-precipitate-particle"
                  style={{
                    '--sink-x': `${p.x}px`, '--sink-start': `${liquidTopY + 4}px`, '--sink-end': `${-6}px`,
                    animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`,
                  } as React.CSSProperties}
                >
                  <rect
                    x={-p.len / 2} y={-p.thick / 2} width={p.len} height={p.thick} rx={p.thick / 2}
                    transform={`rotate(${p.angle})`}
                    fill={i % 3 === 0 ? darken(precColor, 0.3) : precColor}
                    stroke={outline} strokeWidth={0.5}
                  />
                </g>
              ));
            })()}
            </g>
          </g>
        )}

        {/* The splash: a few drops thrown up where the stream is landing and
            falling back, for as long as it is landing there. Few and small —
            a stream poured into a beaker does not spray, and the point of this
            is that the surface is being disturbed, not that there is weather
            in the glassware. Clipped to the vessel like the liquid itself, and
            drawn at whatever height the surface has reached this frame, so it
            climbs with the level. */}
        {landing && hasLiquid && (
          <g clipPath={`url(#${clipId})`} pointerEvents="none">
            {/* The impact itself. A stream arriving on the floor of an empty
                vessel throws up far more than the same stream does a second
                later onto the liquid it has made — the first is water hitting
                glass, the second is water hitting water. So for half a second
                there are three times the drops, thrown wider, and the air the
                stream carried down with it comes back up as bubbles. */}
            {impact && phase === 'splash' && (
              <>
                {IMPACT_DROPS.map((drop, i) => (
                  <circle
                    key={`impact-${i}`}
                    className="chemlab-splash-drop"
                    cx={drop.x * splashScale} cy={rimY * fillFraction} r={drop.r * (splashScale > 1 ? 1.4 : 1)}
                    fill={lighten(liquidFillColor, 0.55)}
                    style={{
                      ['--chemlab-splash-x' as string]: `${drop.dx * splashScale}px`,
                      ['--chemlab-splash-up' as string]: `${-24 * splashScale}px`,
                      animationDelay: `${drop.delay}s`,
                      animationDuration: `${drop.duration}s`,
                    }}
                  />
                ))}
              </>
            )}
            {/* Once there is depth in the vessel the stream stops throwing
                liquid back and starts carrying air down instead: the drops
                give way to bubbles rising through what has been poured. They
                start BELOW the surface and go at it, which is the direction
                that says they are in the liquid rather than over it. */}
            {/* And a fine spatter right at the point of impact — small, close
                in, nothing thrown across the vessel. A stream going into depth
                still breaks the surface where it enters; what it stops doing
                is spraying. */}
            {phase === 'bubbles' && FINE_DROPS.map((drop, i) => (
              <circle
                key={`fine-${i}`}
                className="chemlab-splash-drop"
                cx={drop.x} cy={rimY * fillFraction} r={drop.r}
                fill={lighten(liquidFillColor, 0.5)}
                style={{
                  ['--chemlab-splash-x' as string]: `${drop.dx}px`,
                  ['--chemlab-splash-up' as string]: `${-7 * (splashScale > 1 ? 1.4 : 1)}px`,
                  animationDelay: `${drop.delay}s`,
                  animationDuration: `${drop.duration}s`,
                }}
              />
            ))}
            {/* Rings running out across the surface from the point of entry,
                the whole time the stream is going in. This is the part that
                says something is ARRIVING rather than that a level happens to
                be moving. */}
            {ENTRY_RINGS.map((ring, i) => (
              <ellipse
                key={`ring-${i}`}
                className="chemlab-entry-ring"
                cx={0} cy={rimY * fillFraction}
                rx={Math.max(9, art.rimHalfWidthPx * 0.5)}
                ry={Math.max(9, art.rimHalfWidthPx * 0.5) * VIEW_TILT}
                fill="none" stroke={lighten(liquidFillColor, 0.55)} strokeWidth={1.4}
                style={{ animationDelay: `${ring.delay}s`, animationDuration: `${ring.duration}s` }}
              />
            ))}
            {/* Bubbles for the whole pour, not just its back half: air goes
                down with the stream from the moment it lands. */}
            {POUR_BUBBLES.map((bubble, i) => (
              <circle
                key={`bubble-${i}`}
                className="chemlab-pour-bubble"
                cx={bubble.x} cy={rimY * fillFraction + bubble.rise} r={bubble.r}
                fill="none" stroke={lighten(liquidFillColor, 0.7)} strokeWidth={0.9}
                style={{
                  ['--chemlab-bubble-rise' as string]: `${bubble.rise}px`,
                  animationDelay: `${bubble.delay}s`,
                  animationDuration: `${bubble.duration}s`,
                }}
              />
            ))}
            {phase === 'splash' && SPLASH_DROPS.map((drop, i) => (
              <circle
                key={i}
                className="chemlab-splash-drop"
                cx={drop.x * splashScale} cy={rimY * fillFraction} r={drop.r * (splashScale > 1 ? 1.5 : 1)}
                fill={lighten(liquidFillColor, 0.5)}
                style={{
                  ['--chemlab-splash-x' as string]: `${drop.dx * splashScale}px`,
                  ['--chemlab-splash-up' as string]: `${-17 * splashScale}px`,
                  animationDelay: `${drop.delay}s`,
                  animationDuration: `${drop.duration}s`,
                }}
              />
            ))}
          </g>
        )}

        {/* Where it landed: a ring spreading from the point of impact, drawn
            AFTER the liquid so it sits on the surface rather than half
            sunk behind it. On a dry floor the same mark reads as the dust a
            powder throws up. Held back until the level has all but arrived
            when filling from dry — a ring on a surface that is not there yet
            is a ring in mid-air. */}
        {added && pour && stages.total > 0 && (
          <ellipse
            key={`ripple-${pour.token}`}
            className="chemlab-pour-ripple"
            /* Where the stream ACTUALLY hits: the floor of a vessel that is
               filling, the surface of one that already has something in it. */
            cx={0} cy={pour.intoEmpty ? -2 : (hasLiquid ? liquidTopY : -2)}
            rx={Math.max(10, art.rimHalfWidthPx * 0.55)}
            ry={Math.max(10, art.rimHalfWidthPx * 0.55) * VIEW_TILT}
            fill="none" stroke={lighten(added.color, 0.35)} strokeWidth={2}
            style={{ animationDelay: `${stages.fall}ms` }}
            pointerEvents="none"
          />
        )}

        {/*
          * What is lying in the vessel — drawn as WHAT IT IS.
          *
          * A single mound for everything is right for powder and wrong for
          * everything else: gold strips fell, landed, and were replaced by a
          * spread of fine grains, because the mound was all the bench could
          * draw. So a powder keeps its mound — a loose one, with an irregular
          * top edge, unlike the flat-topped precipitate that settles OUT of a
          * liquid — and anything with pieces is drawn as those pieces, in the
          * exact arrangement they landed in: same generator, same seed.
          *
          * Under the liquid rather than over it, because that is where it is.
          */}
        {restingSolids.length > 0 && (
          <g clipPath={`url(#${clipId})`}>
            {restingSolids.map(({ sub, helping }) => {
              /* The seed carries WHICH helping this is, so a second spatula of
                 the same reagent lands in its own scatter beside the first.
                 Without it both helpings drew at identical positions, exactly
                 overlapping: twice the solid, and no way to tell. */
              const { motion, pieces, mound } = solidPieces(
                seedOf(`${containerId}:${sub.id}:${helping}:${swirls}`), sub.form,
                hasLiquid ? 1.5 : 1, art.floorHalfWidthPx,
              );
              /* Used up as the reaction runs: each piece shrinks toward its
                 own base, as a chip dissolving from the surface does, and is
                 gone when none of that reagent is left. Close to linear: acid
                 eats a chip's surface at a steady depth per second, so its SIZE
                 falls steadily (its mass fastest at first), easing only as the
                 acid itself runs down. */
              const k = sizeForRemaining(remaining[sub.id] ?? 1);
              const consumed: React.CSSProperties | undefined = working ? {
                transform: `scale(${k})`,
                transformBox: 'fill-box',
                transformOrigin: 'center bottom',
                opacity: k > 0 ? 1 : 0,
                transition: `transform ${paceMs}ms cubic-bezier(0.3, 0.3, 0.6, 1), opacity ${paceMs * 0.2}ms ease-in ${paceMs * 0.8}ms`,
              } : undefined;
              if (motion === 'dust' && mound) {
                /*
                 * A HEAP, not a layer.
                 *
                 * A spatula of powder tipped through a mouth lands in a pile
                 * where it fell; it does not spread itself evenly across the
                 * floor, and drawn that way — which is what this did — a flask
                 * of silver chloride read as a flask with a painted white
                 * bottom. Wider and flatter into liquid, since what falls
                 * through water does not stack (see `spread`).
                 *
                 * Drawn as two curves off one crest so the two sides are
                 * unalike: a symmetrical bell reads as a drawn shape rather
                 * than as something that was tipped.
                 */
                const { halfWidth: a, height: h, crest: c } = mound;
                return (
                  <path
                    key={`${sub.id}-${helping}`}
                    className="chemlab-consumed"
                    style={consumed}
                    d={`M ${c - a} 0`
                      + ` C ${c - a * 0.62} ${-h * 0.5} ${c - a * 0.3} ${-h} ${c} ${-h}`
                      + ` C ${c + a * 0.34} ${-h} ${c + a * 0.66} ${-h * 0.44} ${c + a} 0`
                      + ' Z'}
                    fill={sub.color} stroke={darken(sub.color, 0.2)} strokeWidth={0.6}
                  />
                );
              }
              // The group scales, the rect inside keeps its rotation: one
              // transform attribute cannot hold both a tilt and a CSS scale.
              return pieces.map((piece, i) => (
                <g key={`${sub.id}-${helping}-${i}`} className="chemlab-consumed" style={consumed}>
                  <rect
                    x={piece.x - piece.w / 2} y={piece.cy - piece.h / 2}
                    width={piece.w} height={piece.h} rx={piece.rx}
                    transform={`rotate(${piece.spin} ${piece.x} ${piece.cy})`}
                    fill={sub.color} stroke={darken(sub.color, 0.25)} strokeWidth={0.6}
                  />
                </g>
              ));
            })}
          </g>
        )}

        {/* Sheen over glass AND liquid together, so the highlight reads as
            light on the outside of the vessel rather than something floating
            in the middle of the contents. */}
        <path d={art.outlinePath} fill={`url(#${sheenId})`} />

        {/* The specular stripe — the one detail that makes flat vector glass
            read as glass. Clipped, so it simply stops where the vessel
            narrows instead of needing per-shape geometry. Skipped once a
            coloured solution is in the vessel — two opaque white bars
            cutting through a coloured liquid read as paint streaks, not
            glass shine, and the colour itself is the thing to look at then. */}
        {!isColoredSolution && (
          <g clipPath={`url(#${clipId})`}>
            <rect
              x={-art.maxHalfWidthPx * 0.62} y={rimY * 0.9}
              width={Math.max(6, art.maxHalfWidthPx * 0.17)} height={Math.abs(rimY) * 0.86 - 12}
              rx={Math.max(3, art.maxHalfWidthPx * 0.085)} fill="#fff" className="chemlab-glass-shine"
            />
            <rect
              x={-art.maxHalfWidthPx * 0.38} y={rimY * 0.78}
              width={Math.max(3, art.maxHalfWidthPx * 0.075)} height={Math.abs(rimY) * 0.62 - 12}
              rx={Math.max(1.5, art.maxHalfWidthPx * 0.04)} fill="#fff" className="chemlab-glass-shine-soft"
            />
          </g>
        )}

        <VesselDetail vesselType={vesselType} art={art} rimY={rimY} />

        <path
          d={art.outlinePath} fill="none"
          stroke={GLASS_STROKE} strokeWidth={GLASS_STROKE_WIDTH} strokeLinejoin="round"
        />
        {/*
          * The wall takes the colour of what is behind it.
          *
          * Glass is a thin sheet: below the waterline you are looking THROUGH
          * it at the liquid, so the outline there is neither the glass's
          * blue-grey nor the liquid's colour but the two averaged — which is
          * what a beaker of copper sulfate photographs as, blue-green round
          * the bottom and pale grey above the surface. One line all the way
          * down drew a blue frame around green liquid.
          *
          * The same stroke redrawn, clipped to below the surface: the join is
          * exactly at the waterline because it is the same path, and it moves
          * with the level for free while a vessel is filling. A clear liquid
          * barely tints it, for the same reason it barely tints anything.
          */}
        {hasLiquid && (
          <g clipPath={`url(#${belowSurfaceId})`}>
            <path
              d={art.outlinePath} fill="none"
              stroke={mixColors(GLASS_STROKE, liquidFillColor, isClear ? 0.22 : 0.5)}
              strokeWidth={GLASS_STROKE_WIDTH} strokeLinejoin="round"
            />
          </g>
        )}
        {/*
          * A liquid going in: the stream, and the level it makes.
          *
          * The stream is drawn BEFORE the rim so the near lip passes in front
          * of it — it falls down THROUGH the mouth, and a ribbon laid over the
          * rim is a ribbon in front of the vessel. Keyed on the pour's token so
          * a second helping of the same reagent restarts it rather than
          * reusing an element that has already finished.
          *
          * The colour is the reagent's own, off the shelf: this is what is
          * ARRIVING. What it turns into is the mixture's business, and by the
          * time that is known the pour is over.
          *
          * Solids are not here yet — a solid does not pour, and the shape of
          * how it arrives is a different animation.
          */}
        {/* The rim — an annulus between the mouth's outer and inner ellipses,
            which is the glass's own thickness seen from above. This is the
            whole reason the vessel reads as an open container rather than a
            solid shape: a band drawn straight across the top closes the mouth
            off, however thin it is. */}
        <path
          d={art.rimBandPath} fillRule="evenodd" className="chemlab-glass-lip"
          fill={GLASS_LIP} stroke={GLASS_STROKE} strokeWidth={GLASS_RIM_STROKE_WIDTH} strokeLinejoin="round"
        />

        {isSelected && (
          <path d={art.rimOuterPath} fill="none" stroke="#1f8a70" strokeWidth={3.5} />
        )}

        {hasPrecipitate && !isVaporFumes && (() => {
          // Settled solid: an irregular, wavy-topped layer on the floor —
          // mirroring the loose-heap look a poured-in powder has above, since a
          // razor-straight line reads as a painted-on fill rather than
          // something that actually settled. One shade darker than the
          // PRECIPITATE's own reported colour (precColor) — not the
          // liquid's: liquidColor() deliberately shows the mixture's
          // PRE-reaction colour whenever a precipitate is present (see its
          // own comment), which is very often colourless/white even when
          // the solid that came out of it is not (Fe(OH)2 settling pale
          // green out of two colourless solutions is exactly this case) —
          // darkening THAT instead of precColor was quietly turning every
          // such precipitate a flat grey regardless of what colour the
          // model actually reported. EXCEPT when the precipitate is white:
          // "one shade darker than white" is just greyer white, so it stays
          // pure white instead, standing out against the darkened
          // liquidFillColor gives it above.
          // Grows from 0 toward the full depth as precipitateSettledFraction
          // rises — the same decay driving the falling particles, so the
          // settled layer visibly fills in step with them thinning out.
          const depth = ((outcome!.precipitate!.amount === 'heavy' ? 20 : 10) / 2) * precipitateSettledFraction;
          const fill = precIsWhite ? '#ffffff' : darken(precColor, 0.22);
          const w = art.floorHalfWidthPx;
          const k = depth / 9;
          return (
            <g clipPath={`url(#${clipId})`}>
              <path
                d={`M ${-w} 0
                    L ${-w} ${-9 * k}
                    Q ${-w * 0.55} ${-15 * k} ${-w * 0.2} ${-11 * k}
                    Q ${w * 0.1} ${-7 * k} ${w * 0.4} ${-13 * k}
                    Q ${w * 0.75} ${-17 * k} ${w} ${-8 * k}
                    L ${w} 0 Z`}
                fill={fill} stroke={darken(fill, 0.25)} strokeWidth={1}
              />
            </g>
          );
        })()}

        {/* Effervescence: gas coming off the reaction. Small and uniform, in
            streams. Shown whenever the outcome says a gas is evolved, boiling
            or not — a reaction can fizz in a liquid that is also boiling.
            Coloured the same as fumeColor (the puffs escaping above the rim)
            when the gas is visibly coloured (Cl2, NO2, ...) — a plain white
            bubble rising into a colourful cloud read as two different gases
            instead of the same one caught at two points on its way out. */}
        {hasGas && (
          <g
            clipPath={`url(#${clipId})`}
            style={working ? { opacity: runPhase === 'fading' ? 0 : 1, transition: `opacity ${paceMs * 0.4}ms ease-in` } : undefined}
          >
            {bubbles.map((b, i) => (
              <circle
                key={i} cx={b.x} r={fizz.radius} fill={fumeColor ?? '#fff'} stroke="#8fa3b8" strokeWidth={1}
                className="chemlab-bubble"
                style={{ '--bubble-bottom': `${-4}px`, '--bubble-top': `${liquidTopY + 4}px`, animationDelay: `${b.delay}s`, animationDuration: `${b.duration}s` } as React.CSSProperties}
              />
            ))}
          </g>
        )}

        {/* Boiling: big bubbles off the floor, growing as they rise. */}
        {isBoiling && (
          <g clipPath={`url(#${clipId})`}>
            {boilBubbles.map((b, i) => (
              <circle
                key={i} cx={b.x} r={b.r} fill="#fff" opacity={0.85} stroke="#8fa3b8" strokeWidth={1}
                className="chemlab-boil-bubble"
                style={{ '--boil-bottom': '-2px', '--boil-top': `${liquidTopY + 3}px`, animationDelay: `${b.delay}s`, animationDuration: `${b.duration}s` } as React.CSSProperties}
              />
            ))}
          </g>
        )}

        {phPaperColor && (
          // Positioning (translate+rotate) lives on this outer group as a
          // plain SVG attribute; the CSS drop-in animation goes on the
          // inner group instead of here, since a CSS `transform` animation
          // would replace — not compose with — an SVG transform attribute
          // on the same element.
          <g transform={`translate(${art.rimHalfWidthPx * 0.55}, ${(rimY + liquidTopY) / 2}) rotate(15)`}>
            <g className="chemlab-ph-paper-2d">
              <rect x={-5} y={-(rimY - liquidTopY) / 2 - 6} width={10} height={Math.max(20, rimY - liquidTopY + 12)} rx={2} fill={phPaperColor} stroke={LINE} strokeWidth={1} />
              <rect x={-5.5} y={-(rimY - liquidTopY) / 2 - 8} width={11} height={10} rx={2} fill="#f2ede0" stroke={LINE} strokeWidth={1} />
            </g>
          </g>
        )}

        {hasGlassRod && (() => {
          // Opposite side and lean from the pH paper strip so the two never
          // collide if both are present. Longer than the pH paper strip and
          // extends well above the rim — a rod you'd actually hold, not a
          // strip dipped fully in.
          const rodTipY = liquidTopY + 4;
          const rodTopY = rimY - 70;
          const rodLength = rodTipY - rodTopY;
          const coatLength = Math.min(24, rodLength * 0.25);
          return (
            <g transform={`translate(${-art.rimHalfWidthPx * 0.55}, ${(rodTipY + rodTopY) / 2}) rotate(-14)`}>
              <g className="chemlab-glassrod-2d">
                <rect x={-3} y={-rodLength / 2} width={6} height={rodLength} rx={3} fill="#eef2f5" stroke={LINE} strokeWidth={1.5} />
                {glassRodCoating && (
                  <rect
                    x={-4} y={rodLength / 2 - coatLength} width={8} height={coatLength} rx={3}
                    fill={glassRodCoating} stroke={LINE} strokeWidth={1}
                  />
                )}
              </g>
            </g>
          );
        })()}

          {/* The contents label and the reaction card are NOT drawn here.
              SVG has no z-index — paint order is document order — so a card
              rendered inside its own vessel is painted over by every vessel
              drawn after it. They are rendered in a layer of their own, after
              every vessel on the bench (see layer="overlay" below). */}
        {/* There was a compact badge here — a dot on the glass standing in for
            a card the bench had no width to show. The two blocks fold to their
            own icons now, so the stand-in has nothing left to stand in for. */}

        {isExothermic && !hasPipe && (
          // Rises out of the mouth and drifts — deliberately NOT clipped to
          // the vessel outline like bubbles, since smoke escapes above the
          // rim rather than staying inside the liquid. Drawn last (after
          // the label) so it paints on top instead of behind the label's
          // opaque chip, which sits directly in its flight path. Suppressed
          // when a delivery tube leads out of here — see hasPipe's doc.
          <g>
            {smokePuffs.map((p, i) => (
              <circle
                key={i} cx={p.x} cy={rimY - 6} r={p.r} className="chemlab-smoke"
                style={{ '--smoke-dx': `${p.dx}px`, '--smoke-dy': `${p.dy}px`, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s` } as React.CSSProperties}
              />
            ))}
          </g>
        )}

        {/* Steam off the surface, rising up through the neck and out. Not
            clipped — it escapes the vessel, which is the point of it.
            Boiling steams; so does a reaction that gets strongly hot without
            reaching a boil, which is most of what a textbook calls "the flask
            becomes hot" and which the bench used to draw as nothing at all. */}
        {(isBoiling || isSteaming) && (
          <g>
            {steamPuffs.map((p, i) => (
              <circle
                key={i} cx={p.x} cy={liquidTopY - 4} r={p.r} className="chemlab-steam"
                style={{ '--steam-dx': `${p.dx}px`, '--steam-dy': `${p.dy}px`, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s` } as React.CSSProperties}
              />
            ))}
          </g>
        )}

        {(fumeColor ?? (isVaporFumes ? precColor : null)) && !hasPipe && (
          // Suppressed when a delivery tube leads out of here — see
          // hasPipe's doc on VesselProps. isVaporFumes reuses this same rim
          // cloud for the ammonia/HCl-style "dense white fumes" case — see
          // isVaporFumes's own doc for why the floor layer is wrong there
          // (no liquid surface to rise from, so it keeps starting at the rim).
          // A genuine gas evolving from a reaction in the liquid instead
          // starts at the SURFACE and rises the full neckGapPx through the
          // empty glass before it ever reaches the rim — starting it at the
          // rim left that whole stretch looking gas-free.
          <g>
            {fumesPuffs.map((p, i) => (
              <circle
                key={i} cx={p.x} cy={isVaporFumes ? rimY - 6 : liquidTopY - 4} r={p.r} className="chemlab-fumes"
                style={{
                  fill: fumeColor ?? precColor,
                  '--fumes-dx': `${p.dx}px`,
                  '--fumes-dy': `${isVaporFumes ? p.dy : p.dy - neckGapPx}px`,
                  animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`,
                } as React.CSSProperties}
              />
            ))}
          </g>
        )}
      </g>
      </g>
    </g>
  );
}

export function ChemistryLabScene2D({
  containers, selectedIndex, onSelect, onSetTemperature, pour, swirl, uiScale, showQuantities,
  onStandMenu, onRequestMenu,
}: ChemistryLabScene2DProps): React.JSX.Element {
  /* Per instance: the mask's contents are this bench's vessels, so two scenes
     on one page (admin previews one beside a list) must not share an id. */
  const sceneId = useId();
  const benchLineMaskId = `chemlab-bench-line-${sceneId}`;
  const benchSeamId = `chemlab-bench-seam-${sceneId}`;
  const contactShadowId = `chemlab-contact-${sceneId}`;
  // How many columns fit across depends on the panel's width, so the scene has
  // to know how wide its scroll box is.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Measure once, now. A ResizeObserver is deferred in a background tab, so
    // relying on it alone leaves the box at zero — and a zero box sends every
    // fit calculation to its fallback, which is how the scale came to depend
    // on nothing but the container count.
    const first = el.getBoundingClientRect();
    setBox(prev => (prev.width === first.width && prev.height === first.height
      ? prev : { width: first.width, height: first.height }));
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setBox(prev => (prev.width === width && prev.height === height ? prev : { width, height }));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /**
   * Where the two blocks beside each vessel sit, and whether they are folded.
   *
   * View state, so it lives here rather than on LabContainer: nothing about a
   * reaction changes because a box was dragged or folded, and putting it on the
   * container meant every path that produced an outcome — the cached one, the
   * LLM one, the deterministic one — had to remember to fold boxes too. Keyed
   * by container id rather than index so it survives a removal from the middle.
   */
  const [blocks, setBlocks] = useState<Record<string, { info: BlockState; reaction: BlockState }>>({});
  const blockStateFor = (id: string) => blocks[id] ?? EMPTY_BLOCKS;
  const setBlock = (id: string, kind: 'info' | 'reaction', next: BlockState) =>
    setBlocks(prev => ({ ...prev, [id]: { ...(prev[id] ?? EMPTY_BLOCKS), [kind]: next } }));

  /**
   * A new reaction opens its own vessel's blocks and folds everyone else's.
   *
   * The bench fills up with cards nobody is reading any more, and the one that
   * just happened is the one worth the width. Written as an OBSERVATION of the
   * outcomes that arrive — not as something the chemistry calls — so no result
   * path has to know a layout rule exists. Identity, not deep equality: a new
   * outcome object is a new result, and re-running the same mix produces one.
   */
  const seenOutcomes = useRef<Map<string, ChemistryLabOutcome | undefined>>(new Map());
  useEffect(() => {
    const changed = containers.find(c => c.outcome && seenOutcomes.current.get(c.id) !== c.outcome);
    const seen = new Map<string, ChemistryLabOutcome | undefined>();
    containers.forEach(c => seen.set(c.id, c.outcome));
    seenOutcomes.current = seen;
    if (!changed) return;
    setBlocks(prev => {
      const next: Record<string, { info: BlockState; reaction: BlockState }> = {};
      for (const c of containers) {
        const cur = prev[c.id] ?? EMPTY_BLOCKS;
        const minimised = c.id !== changed.id;
        // Only the fold is touched. A box the student dragged somewhere stays
        // where they put it, folded or not.
        next[c.id] = {
          info: { ...cur.info, minimised },
          reaction: { ...cur.reaction, minimised },
        };
      }
      return next;
    });
  }, [containers]);

  // A container with no burner can't be heated regardless of its stored
  // temperature (the page resets it to ambient the moment a burner is removed)
  // — this is just the visual belt-and-braces match.
  const heat = containers.map(c => (c.burnerType === 'none' ? 0 : heatFraction(c.temperatureC)));
  const { columns, placements } = benchLayout(
    containers.map(c => ({ vesselType: c.vesselType, standType: c.standType })),
  );

  // The tallest column decides how much headroom a shelf needs above its
  // bench line — two flasks stacked on one rod stand well above the height a
  // single vessel ever needs.
  const tallest = Math.max(
    ...containers.map((c, i) => Math.max(
      placements[i].rise * PX_SCALE + VESSEL_2D[c.vesselType].overallHeightPx,
      // A thermometer can stand taller than a short vessel beside it.
      c.burnerType === 'none' ? 0 : BENCH_TEMP_LIFT + BENCH_TEMP_HEIGHT + 18,
    ) + LABEL_HEADROOM),
  );
  // A delivery tube arcs above the mouths it joins, so a shelf has to clear
  // that too or a tube would run through the apparatus standing above it.
  const tubeHeadroom = containers.some(c => c.pipeToId) ? TUBE_MIN_LIFT : 0;

  /*
   * Required width is the content's OWN width, not a count. Four test tubes
   * fit where two flasks do not, because they are narrower — which is correct
   * and explains itself, where a uniform pitch made "number of items" look
   * like the input.
   *
   * A column's half-width accounts for any sideways offset within it, so a
   * pair of vessels sharing a multi-level stand reserves what it actually
   * spans rather than one vessel's width.
   *
   * The label and reaction card reserve NOTHING. They are overlays and may
   * overlap — the student folds down the ones they are not reading, and a new
   * reaction folds the rest for them. Holding 126px open on every vessel's
   * left and 290px on its right budgeted for chrome that is mostly not there — a lone flask reserved
   * 807px around 224px of glassware and rendered at 27% of the panel where
   * MAX_ZOOM allowed 40%. There is no edge allowance either; the frame margins
   * are the edge clearance, and the shelf is centred within them.
   */
  const columnHalfWidth = columns.map(members => Math.max(
    ...members.map(i => Math.abs(placements[i].offsetX * PX_SCALE) + VESSEL_2D[containers[i].vesselType].maxHalfWidthPx),
  ));
  /** What a column occupies on the shelf: its own width plus its padding. */
  const footprint = columnHalfWidth.map(half => half * 2 + ITEM_PAD);

  const frameW = box.width > 0 ? box.width * (1 - MARGIN_X * 2) : 0;
  const frameH = box.height > 0 ? box.height * (1 - MARGIN_BOTTOM) : 0;
  const maxZoom = frameH > 0
    ? Math.max(MIN_ZOOM, (MAX_VESSEL_SHARE_OF_HEIGHT * box.height) / TALLEST_VESSEL_PX)
    : MIN_ZOOM;

  /**
   * What one arrangement of the bench would cost, in both axes.
   *
   * Neither fit has a floor under it. A floor on required content is a lie
   * about the content that becomes a cap on the scale, and it hides:
   * MIN_CONTENT_HEIGHT (420) against a real 183 quietly became the binding
   * constraint and held a lone flask at 1.183 where the ceiling allowed 1.518.
   */
  const arrangement = (per: number) => {
    const rows = Math.max(1, Math.ceil(columns.length / per));
    const widthOf = (row: number) =>
      footprint.slice(row * per, (row + 1) * per).reduce((sum, w) => sum + w, 0);
    const reqW = Math.max(...Array.from({ length: rows }, (_, r) => widthOf(r)), 0);
    const reqH = (tallest + tubeHeadroom + TOP_PADDING) * rows;
    const fw = frameW > 0 ? frameW / Math.max(reqW, 1) : maxZoom;
    const fh = frameH > 0 ? frameH / Math.max(reqH, 1) : maxZoom;
    return { per, rows, reqW, reqH, fw, fh, fit: Math.min(fw, fh) };
  };

  /*
   * The upper rack opens when one shelf can no longer hold the bench legibly —
   * not at a fixed count, and not for a marginal gain.
   *
   * A count was the wrong question: four test tubes sit on one shelf
   * comfortably and two wide flasks on a narrow panel do not. But "split
   * whenever it zooms larger" is wrong in the other direction — at three
   * flasks a second rack bought 1.3% and reshuffled the whole bench for it.
   *
   * So MIN_ZOOM is the trigger, exactly as it is for refusal: while one shelf
   * still reads, one shelf it stays. A second rack halves the width needed and
   * doubles the height, so it is only tried when width is what is binding, and
   * only kept if it actually helps.
   *
   * Two racks is the ceiling: a third would be shelves of glassware too small
   * to read, which is the thing MIN_ZOOM exists to say.
   */
  /*
   * Two limits, and the tighter one wins.
   *
   * MAX_PER_RACK is a hard cap: a wide panel will happily zoom seven flasks
   * into one row, and they read as a queue rather than a shelf. MIN_ZOOM is the
   * soft one: while a rack still reads, it keeps what it has.
   *
   * Rows are filled EVENLY rather than packed to the cap — six vessels are
   * three and three, not five and one, which is what "a pair of shelves" looks
   * like.
   */
  /*
   * Capped at two racks, because only two have positions on the bench
   * (shelfFractionFromBottom knows row 0 and row 1). Past ten vessels the cap
   * has to give rather than the layout: a third rack would be drawn on top of
   * the second, which is worse than a row of six.
   */
  const minRows = Math.min(2, Math.max(1, Math.ceil(columns.length / MAX_PER_RACK)));
  const evenly = (rows: number) => arrangement(Math.max(1, Math.ceil(columns.length / rows)));
  const base = evenly(minRows);
  const roomier = minRows < 2 ? evenly(2) : base;
  const chosen = base.fit < MIN_ZOOM && roomier.fit > base.fit ? roomier : base;

  const perShelf = chosen.per;
  const shelves = chosen.rows;
  const requiredContentWidth = chosen.reqW;
  const requiredContentHeight = chosen.reqH;
  const fitWidth = chosen.fw;
  const fitHeight = chosen.fh;
  /*
   * The scene ALWAYS fits: there is no lower clamp here, so the bench never
   * scrolls. MIN_ZOOM is a threshold for what to do about a bench that has
   * outgrown its panel — promote to the upper rack, then refuse the next
   * vessel — not a floor on rendering. Clamping up to it instead is what drew
   * the bench wider than the panel and produced a scrollbar.
   *
   * The one case where the scale is allowed below MIN_ZOOM is a bench that was
   * already populated when the window shrank: the alternative is dropping a
   * vessel someone placed, which is worse than small glassware.
   */
  const fit = Math.min(fitWidth, fitHeight);
  const zoom = Math.min(maxZoom, fit);
  /**
   * How much bigger the blocks are than the units they are written in.
   *
   * The bench's type is held at an absolute size by dividing the zoom out of
   * it (see LABEL_TARGET_PX), and the SLOTS THE TYPE SITS IN have to move with
   * it — a card whose words grew by half inside a box that did not simply
   * spills out of the box, which is exactly what happened the first time this
   * was tried: the contents block vanished, clipped by its own foreignObject.
   * So one factor sizes both, and every block dimension below is expressed
   * through it.
   */
  const textScale = (LABEL_TARGET_PX * uiScale) / (LABEL_BASE_PX * zoom);
  /** ⚠ Consumed once refusal is built: the bench has outgrown its panel. */
  const belowLegibleZoom = fit < MIN_ZOOM;
  void belowLegibleZoom;
  // The -1 is deliberate: contentWidth*zoom rounding a fraction of a px
  // above box.width was enough to trip the scroll box's `overflow: auto`
  // into showing a scrollbar even when nothing was actually cut off.
  const usableWidth = box.width > 0 ? (box.width - 1) / zoom : UNMEASURED_FRAME;

  // Only widen the canvas past the viewport for what's actually on the
  // busiest shelf — reserving room for a full shelf regardless of occupancy
  // pushed a lone container off-centre (it was centred in a canvas sized for
  // three, not in the visible box). A single container still gets the
  // viewport's full width to centre in; three does the same thing three
  // containers always did.
  const contentWidth = Math.max(usableWidth, requiredContentWidth);

  // A bench line sits at a fixed FRACTION of the visible height up from the
  // bottom, not a fixed pixel pitch — a single shelf sits low and central;
  // adding a second shelf pulls the first one further down (10% instead of
  // 20%) and the new one appears higher up (55%), so the pair reads as
  // stacked rather than the bottom one just nudging over. Two racks is the
  // ceiling (see the arrangement choice above), so nothing past `row 1`
  // needs a fraction of its own.
  // On a phone-sized scene a single bench sits lower: the screen is short and
  // the pane below already holds the bench up, so a fifth of the height left
  // a band of empty table under the flask and pushed the glassware toward the
  // top bar.
  const lowBench = box.width > 0 && box.width <= 640;
  // On a phone the bench's own controls (lighting, A / A, start over) sit in
  // a row along the bottom edge, and belong UNDER the floor the glassware
  // stands on, not across it — so the bottom bench line is kept at least
  // that row's height up: 6px inset + 30px buttons + 6px of table above
  // them. In screen pixels, so a fraction of the measured box.
  const underControls = lowBench && box.height > 0 ? 42 / box.height : 0;
  const shelfFractionFromBottom = (row: number): number => {
    if (shelves <= 1) return Math.max(lowBench ? 0.14 : 0.20, underControls);
    // Two shelves: the lower one sits at 10%, not 5% — low enough to read as
    // the bottom of the bench, high enough not to look pinned to the frame.
    return row === 0 ? Math.max(0.10, underControls) : 0.55;
  };
  const topmostFraction = shelfFractionFromBottom(shelves - 1);
  const bottomFraction = shelfFractionFromBottom(0);
  // contentHeight normally just fills the visible box (usableHeight) so the
  // fractions above mean what they say — but on a viewport too short for
  // that, filling it literally would crush the apparatus above the topmost
  // bench line or the stand clearance below the bottom one, so this floors
  // contentHeight at whatever satisfies both instead.
  const minContentHeight = Math.max(
    (tallest + tubeHeadroom + TOP_PADDING) / (1 - topmostFraction),
    BELOW_BENCH / bottomFraction,
  );
  const usableHeight = box.height > 0 ? (box.height - 1) / zoom : UNMEASURED_FRAME;
  // A busy bench fills whatever height it's given — more shelves may
  // legitimately need it. But stretching to fill it is exactly wrong for one
  // or two containers: the box is often much taller than a single flask
  // needs, and filling it anyway is what buried the (now-bigger, see zoom
  // above) apparatus under a sky of empty canvas — the width zoom-in alone
  // never fixed that, because it never touched how much vertical space the
  // scene claimed. Capped to its own tightest fit instead, so the canvas is
  // no taller than the apparatus actually needs and the bottom-anchored
  // scroll container (see its own CSS) is what shows it sitting low with a
  // normal amount of headroom, not a screen's worth of it.
  /*
   * Fill the height rather than shrink-wrapping the apparatus. The bench line
   * sits at a FRACTION of this, so if the scene is shorter than the panel the
   * fraction is measured against the scene and the bench does not land where
   * the number says — 20% of a 229-tall scene inside a 585-tall panel is not
   * 20% of what anyone can see.
   */
  const contentHeight = Math.max(usableHeight, minContentHeight);
  /** Bench line of shelf `row`; row 0 is the bottom one, and shelves stack up. */
  const benchYOf = (row: number): number => contentHeight * (1 - shelfFractionFromBottom(row));

  // Where each column sits, and where each vessel's mouth ends up — the tubes
  // are drawn across the whole bench, so they need positions the column groups
  // can't hand them.
  const columnGeom = columns.map((_, col) => {
    const row = Math.floor(col / perShelf);
    const indexInRow = col % perShelf;
    // Each shelf centres its own contents, so a part-filled top shelf sits over
    // the middle of the one below rather than hugging the left edge.
    // Laid out by actual widths: each column sits a gap clear of its
    // neighbour's edge, and the shelf as a whole is centred.
    const first = row * perShelf;
    const onThisShelf = Math.min(perShelf, columns.length - first);
    const widths = footprint.slice(first, first + onThisShelf);
    let x = contentWidth / 2 - widths.reduce((sum, w) => sum + w, 0) / 2;
    for (let k = 0; k < indexInRow; k++) x += widths[k];
    // The item sits in the middle of its own footprint, so its padding falls
    // half to each side and neighbours land ITEM_PAD apart.
    return { x: x + widths[indexInRow] / 2, benchY: benchYOf(row) };
  });

  const rims = new Map<string, { x: number; y: number }>();
  columns.forEach((members, col) => {
    members.forEach(i => {
      const c = containers[i];
      rims.set(c.id, {
        x: columnGeom[col].x + placements[i].offsetX * PX_SCALE,
        y: columnGeom[col].benchY - placements[i].rise * PX_SCALE - VESSEL_2D[c.vesselType].heightPx,
      });
    });
  });

  return (
    <div className="chemlab-scene-scroll" ref={scrollRef}>
      <svg
        className="chemlab-scene-2d"
        width={contentWidth * zoom} height={contentHeight * zoom}
        viewBox={`0 0 ${contentWidth} ${contentHeight}`}
        style={{
          // The zoom is divided out and then applied again by the SVG itself,
          // so what lands on screen is LABEL_TARGET_PX at the lab's scale,
          // whatever the bench is doing.
          '--chemlab-text-scale': textScale,
        } as React.CSSProperties}
      >
      <defs>
        {/*
          * What you see down a mouth, and it is not a shadow.
          *
          * At this eye height the far wall hides the floor, so the whole
          * ellipse is a band of the FAR WALL'S INNER SURFACE. Two things
          * follow, and the first is why an earlier version of this looked
          * like a lid:
          *
          * - It is CLEARER than the glass below it, not murkier. A ray through
          *   the opening crosses one wall; a ray through the body crosses two,
          *   near and far. So the top of the ellipse is lifted, not darkened.
          * - What darkening there is belongs at the NEAR edge. The top of the
          *   ellipse is the far wall just under its own rim — closest to the
          *   opening and the most lit. The bottom is the deepest part of the
          *   interior still in view, with the near wall standing between it
          *   and the light.
          *
          * One gradient for every vessel on the bench: objectBoundingBox units
          * mean each mouth's own ellipse is the 0..1 it runs across. Which
          * themes use it is set in the stylesheet (--lab-glass-bore-fill).
          */}
        <linearGradient id="chemlab-bore-shade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.45" />
          <stop offset="35%" stopColor="#fff" stopOpacity="0.1" />
          <stop offset="62%" stopColor="#000" stopOpacity="0.04" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.2" />
        </linearGradient>
        {/* Where the bench's surface meets the wall behind it. A drawn line
            said the same thing, but a 2.5px stroke is a stroke — a rule ruled
            across a picture — where what is actually there is the dark seam a
            flat surface makes as it recedes into the corner. Strongest at the
            seam, gone within a few pixels of it. */}
        <linearGradient id={benchSeamId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: 'var(--lab-bench-line)' }} stopOpacity="0.7" />
          <stop offset="100%" style={{ stopColor: 'var(--lab-bench-line)' }} stopOpacity="0" />
        </linearGradient>
        {/* What actually says a thing is STANDING on the bench rather than
            floating in front of it: the shadow it casts at its own foot. Soft
            to its edge, so it reads as contact rather than as a painted
            ellipse — which is what a flat fill at this size looks like. */}
        <radialGradient id={contactShadowId}>
          <stop offset="0%" stopColor="#000" stopOpacity="0.2" />
          <stop offset="55%" stopColor="#000" stopOpacity="0.09" />
          <stop offset="100%" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="chemlab-bench-bg" x1="0" y1="0" x2="0" y2="1">
          {/* Set through style, not the attribute: a presentation attribute
              does not take var(), a CSS property does — and the bench has to
              follow the page's theme now that it is not always dark. */}
          <stop offset="0%" style={{ stopColor: 'var(--lab-bench-bg)' }} />
          <stop offset="100%" style={{ stopColor: 'var(--lab-bench-bg-low)' }} />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={contentWidth} height={contentHeight} fill="url(#chemlab-bench-bg)" />

      {/* The bench line stops at the glassware rather than running through it.
          The glass is only a fifth opaque, so a line drawn straight across
          showed through every vessel standing on it — and a line you can read
          through a beaker is a line drawn ON the beaker, which is the one
          thing the back edge of a bench is not. Cut out of the line rather
          than painted over it: the vessels themselves stay see-through, which
          is what makes them glass.

          userSpaceOnUse with the scene's own box, because a mask's default
          region is a margin around the masked element's bbox — and a
          horizontal line's bbox is a strip 2.5px tall, which would clip the
          vessel shapes to nothing. */}
      <mask id={benchLineMaskId} maskUnits="userSpaceOnUse" x={0} y={0} width={contentWidth} height={contentHeight}>
        <rect x={0} y={0} width={contentWidth} height={contentHeight} fill="#fff" />
        {columns.map((members, col) => members.map(i => (
          <path
            key={containers[i].id}
            transform={`translate(${columnGeom[col].x + placements[i].offsetX * PX_SCALE}, ${columnGeom[col].benchY - placements[i].rise * PX_SCALE})`}
            d={VESSEL_2D[containers[i].vesselType].outlinePath}
            fill="#000"
          />
        )))}
      </mask>
      <g mask={`url(#${benchLineMaskId})`}>
        {Array.from({ length: shelves }, (_, row) => (
          <rect
            key={row} x={0} y={benchYOf(row)} width={contentWidth} height={BENCH_SEAM_H}
            fill={`url(#${benchSeamId})`}
          />
        ))}
      </g>

      {/* One per column, at the foot of whatever is standing there — the
          vessel's own base, or the tripod's legs, or a stand's plate. Wider
          than the thing above it, and much flatter: a shadow on a surface seen
          at this angle is a squashed ellipse, not a circle. Drawn before the
          apparatus, so everything stands ON its own shadow. */}
      {columns.map((members, col) => {
        const half = columnHalfWidth[col] * 1.2;
        return (
          <ellipse
            key={`shadow-${containers[members[0]].id}`}
            cx={columnGeom[col].x} cy={columnGeom[col].benchY + 1}
            rx={half} ry={half * VIEW_TILT * 0.45}
            fill={`url(#${contactShadowId})`}
          />
        );
      })}

      {DEBUG_LAYOUT && (
        <g pointerEvents="none" fontSize={11} fontFamily="monospace">
          {/* The frame the percentage margins leave. Those are a share of the
              PANEL, so they divide by zoom to land in scene units. */}
          {(() => {
            const mx = box.width > 0 ? (box.width * MARGIN_X) / zoom : 0;
            const mb = box.height > 0 ? (box.height * MARGIN_BOTTOM) / zoom : 0;
            return (
              <>
                <rect
                  x={mx} y={0} width={Math.max(0, contentWidth - mx * 2)} height={Math.max(0, contentHeight - mb)}
                  fill="none" stroke="#4ade80" strokeWidth={1.5} strokeDasharray="8 6"
                />
                <text x={mx + 4} y={14} fill="#4ade80">frame {Math.round(contentWidth - mx * 2)}×{Math.round(contentHeight - mb)} (margins {Math.round(mx)} / {Math.round(mb)})</text>
              </>
            );
          })()}

          {/* Each column: its footprint, and the vessel's own width inside it.
              The difference between the two boxes is ITEM_PAD, half a side. */}
          {columnGeom.map((g, col) => {
            const fp = footprint[col];
            const half = columnHalfWidth[col];
            const top = g.benchY - tallest;
            return (
              <g key={`fp-${col}`}>
                <rect x={g.x - fp / 2} y={top} width={fp} height={tallest}
                  fill="none" stroke="#60a5fa" strokeWidth={1.2} strokeDasharray="6 4" />
                <rect x={g.x - half} y={top} width={half * 2} height={tallest}
                  fill="none" stroke="#fbbf24" strokeWidth={1.2} strokeDasharray="3 3" />
                <line x1={g.x} y1={top} x2={g.x} y2={g.benchY} stroke="#60a5fa" strokeWidth={0.8} strokeDasharray="2 4" />
                <text x={g.x - fp / 2 + 2} y={top - 4} fill="#60a5fa">fp {Math.round(fp)} = w {Math.round(half * 2)} + pad {ITEM_PAD}</text>
              </g>
            );
          })}

          <text x={6} y={contentHeight - 8} fill="#94a3b8">
            zoom {zoom.toFixed(3)} · fitW {fitWidth.toFixed(3)} · fitH {fitHeight.toFixed(3)} · max {maxZoom.toFixed(3)} · min {MIN_ZOOM.toFixed(3)} · req {Math.round(requiredContentWidth)}×{Math.round(requiredContentHeight)} · bench {box.width}×{box.height}
          </text>
        </g>
      )}

      <DeliveryTubes containers={containers} rims={rims} />

      {columns.map((members, col) => {
        const { x: columnX, benchY } = columnGeom[col];
        const base = containers[members[0]];
        const standType = base.standType;
        const standMembers = members.map(i => ({
          art: VESSEL_2D[containers[i].vesselType],
          placement: placements[i],
          burnerType: placements[i].canHeat ? containers[i].burnerType : ('none' as const),
          // Lit whenever the burner is set above ambient — a burner turned up
          // under an empty vessel is still burning, and hiding the flame until
          // something is poured in reads as the control not working. The
          // fraction, not a flag: the flame's size is how the bench shows
          // where on the scale the slider sits.
          heat: heat[i],
        }));
        return (
          <g key={base.id} transform={`translate(${columnX}, ${benchY})`}>
            {/* Both stand layers share one right-click target. The glassware
                stops propagation on its own, so a right-click reaches this
                only when it landed on the apparatus rather than a vessel. */}
            <LongPressGroup open={(x, y) => onStandMenu(members[0], x, y)}>
              <ColumnStand standType={standType} members={standMembers} layer="behind" />
            </LongPressGroup>
            {members.map(i => {
              const c = containers[i];
              return (
                <VesselIllustration
                  key={c.id}
                  containerId={c.id}
                  vesselType={c.vesselType}
                  working={[...c.steps].reverse().find(st => st.stoichiometry)?.stoichiometry}
                  risePx={placements[i].rise * PX_SCALE}
                  offsetXPx={placements[i].offsetX * PX_SCALE}
                  hasGlassRod={c.hasGlassRod}
                  glassRodCoating={c.glassRodSubstanceId ? findSubstance(c.glassRodSubstanceId)?.color ?? null : null}
                  contents={c.contents}
                  textScale={textScale}
                  showQuantities={showQuantities}
                  swirlToken={swirl?.index === i ? swirl.token : null}
                  swirls={c.swirls}
                  outcome={c.outcome}
                  celsius={c.temperatureC}
                  // Boiling is a property of what's IN the vessel, not of how
                  // hard the burner is driven: the mixture's own boiling point
                  // comes back with the outcome. An empty vessel over a flame
                  // has nothing to boil, however hot it gets.
                  isBoiling={c.contents.length > 0 && isBoilingNow(
                    c.temperatureC, c.outcome?.boilingPointC,
                    c.outcome?.temperatureChange?.band, heat[i] > 0,
                  )}
                  hasPipe={!!c.pipeToId || containers.some(o => o.pipeToId === c.id)}
                  isSelected={i === selectedIndex}
                  pour={pour?.index === i
                    ? { substanceId: pour.substanceId, token: pour.token, intoEmpty: pour.intoEmpty, ms: pour.ms }
                    : undefined}
                  columnXPx={columnX}
                  sceneWidthPx={contentWidth}
                  // Full label + reaction card for every column at once is
                  blocks={blockStateFor(c.id)}
                  onBlockChange={(kind, next) => setBlock(c.id, kind, next)}
                  zoom={zoom}
                  loading={c.loading}
                  phPaperColor={c.phPaperApplied && c.outcome?.pHBand ? pHBandToHex(c.outcome.pHBand) : null}
                  onClick={() => onSelect(i)}
                  onRequestMenu={(x, y) => onRequestMenu(i, x, y)}
                />
              );
            })}

            {/* Whatever physically sits in front of the glassware — a clamp's
                jaw, a rack's front rail. */}
            <LongPressGroup open={(x, y) => onStandMenu(members[0], x, y)}>
              <ColumnStand standType={standType} members={standMembers} layer="front" />
            </LongPressGroup>

            {/* The dial belongs to the flame, so it sits under it on the
                bench — and only the lowest vessel in a column can have one. */}
            {/* Stands beside the apparatus it measures, on the side a retort
                rod never occupies. */}
            {base.burnerType !== 'none' && (
              <g transform={`translate(${-(VESSEL_2D[base.vesselType].maxHalfWidthPx + BENCH_TEMP_OFFSET)}, ${-BENCH_TEMP_LIFT})`}>
                <BenchTemperature
                  celsius={base.temperatureC}
                  disabled={base.loading}
                  gradientId={`temp-scale-${base.id}`}
                  onSet={(celsius) => onSetTemperature(members[0], celsius)}
                />
              </g>
            )}
          </g>
        );
      })}

      {/*
        * Every card on the bench, drawn after every vessel on it.
        *
        * SVG has no z-index: paint order is document order. A card rendered inside
        * its own vessel was painted over by any vessel drawn later — the third
        * test tube and its smoke sat on top of the second's reaction card. Same
        * positions and transforms as before; only the moment they are drawn has
        * changed.
        */}
      {columns.map((members, col) => (
        <g
          key={`cards-${containers[members[0]].id}`}
          transform={`translate(${columnGeom[col].x}, ${columnGeom[col].benchY})`}
        >
          {members.map(i => {
            const c = containers[i];
            return (
              <VesselIllustration
                key={c.id}
                layer="overlay"
                /* The most recent step that has working. A dip computes none,
                   and the vessel's numbers are still the last mix's. */
                working={[...c.steps].reverse().find(st => st.stoichiometry)?.stoichiometry}
                containerId={c.id}
                vesselType={c.vesselType}
                risePx={placements[i].rise * PX_SCALE}
                offsetXPx={placements[i].offsetX * PX_SCALE}
                hasGlassRod={c.hasGlassRod}
                glassRodCoating={null}
                contents={c.contents}
                textScale={textScale}
                showQuantities={showQuantities}
                swirls={c.swirls}
                outcome={c.outcome}
                celsius={c.temperatureC}
                isBoiling={false}
                hasPipe={!!c.pipeToId || containers.some(o => o.pipeToId === c.id)}
                isSelected={i === selectedIndex}
                columnXPx={columnGeom[col].x}
                sceneWidthPx={contentWidth}
                blocks={blockStateFor(c.id)}
                onBlockChange={(kind, next) => setBlock(c.id, kind, next)}
                zoom={zoom}
                loading={c.loading}
                phPaperColor={null}
                onClick={() => undefined}
                /* The overlay pass draws the blocks, and the contents block
                   carries the ⋯ that opens this vessel's menu — so this pass
                   needs the real callback, not the no-op the glassware's own
                   handlers made it look like it could get away with. */
                onRequestMenu={(x, y) => onRequestMenu(i, x, y)}
              />
            );
          })}
        </g>
      ))}

      </svg>
    </div>
  );
}
